import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { useAuth } from "../AuthContext";
import api from "../api";
import Modal from "../components/Modal";
import FileAttachment from "../components/FileAttachment";
import FileManager from "./FileManager";
import { FiBarChart2, FiColumns, FiInfo, FiList, FiRefreshCw, FiSliders, FiTag } from "react-icons/fi";
import {
  DEFAULT_REGISTRATION_PAGE_ICON,
  REGISTRATION_PAGE_ICON_OPTIONS,
  resolveRegistrationPageIcon,
} from "../utils/registrationPages";
ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleString("pt-BR");
  } catch (error) {
    return "-";
  }
};

const stableStringify = (value) => {
  if (value === null || value === undefined) {
    return JSON.stringify(value);
  }
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return `[${value.map((item) => stableStringify(item)).join(",")}]`;
    }
    const keys = Object.keys(value).sort();
    return `{${keys
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
};

const isPrimitive = (value) =>
  value === null ||
  value === undefined ||
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean";

const resolveFieldLabel = (field) => {
  if (!field) return "Campo";
  if (typeof field.label === "string" && field.label.trim()) {
    return field.label.trim();
  }
  if (typeof field.name === "string" && field.name.trim()) {
    return field.name.trim();
  }
  if (typeof field.placeholder === "string" && field.placeholder.trim()) {
    return field.placeholder.trim();
  }
  return field.id || "Campo";
};

const FIELD_TYPES_WITH_OPTIONS = new Set(["select", "radio", "dropdown"]);

const FILE_FIELD_TYPES = new Set(["file", "attachment", "upload"]);

const unsupportedFieldTypes = new Set([
  "file",
  "attachment",
  "upload",
  "signature",
  "table",
  "subform",
  "dynamicSelection",
]);

const fieldVisible = (field, values) => {
  if (!field || typeof field !== "object") {
    return false;
  }

  const expression =
    typeof field.visibleWhen === "string" ? field.visibleWhen.trim() : "";

  if (!expression) {
    return true;
  }

  try {
    const fn = new Function(
      "values",
      `with (values) { return (${expression}); }`,
    );
    return !!fn(values || {});
  } catch (error) {
    console.error("Erro ao avaliar condição de visibilidade do campo", error);
    return false;
  }
};

const STATUS_LABELS = {
  ACTIVE: "Em andamento",
  COMPLETED: "Concluído",
};

const COLUMN_SOURCE_LABELS = {
  form: "Formulário",
  metadata: "Campos extras",
  system: "Dados do sistema",
};

const REPORT_NOTE =
  "Relatório gerado pelo sistema Intelligent BPM Nash Tecnologia. Site: www.nashtec.com.br";

const RECORD_SHEET_NOTE =
  "Ficha gerada pela Central de Cadastros do Intelligent BPM Nash Tecnologia.";
const DASHBOARD_FIELD_SELECTION_STORAGE_KEY = "registration-center-dashboard-selected-fields";

const NAVY_HEX = "#0b1f3a";

const sanitizeFilenamePart = (value) => {
  if (!value) return "relatorio";
  return value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase()
    .slice(0, 40) || "relatorio";
};

const buildReportFilename = (processName, extension) => {
  const base = sanitizeFilenamePart(processName);
  const timestamp = new Date()
    .toISOString()
    .replace(/[-:]/g, "")
    .replace("T", "-")
    .slice(0, 15);
  return `${base}-${timestamp}.${extension}`;
};

const buildRecordSheetFilename = (processName, instanceId) => {
  const base = sanitizeFilenamePart(processName || "cadastro");
  const identifier =
    instanceId !== undefined && instanceId !== null
      ? `solicitacao-${instanceId}`
      : "cadastro";
  const timestamp = new Date()
    .toISOString()
    .replace(/[-:]/g, "")
    .replace("T", "-")
    .slice(0, 15);
  return `${base}-${sanitizeFilenamePart(identifier)}-${timestamp}.pdf`;
};

const createSummaryMap = (instance) => {
  if (!instance || !Array.isArray(instance.formSummary)) {
    return new Map();
  }
  return new Map(
    instance.formSummary
      .filter((entry) => entry && typeof entry.id === "string")
      .map((entry) => [entry.id, entry.value ?? ""]),
  );
};

const resolveInstanceSummaryValue = (instance, column, summaryMap) => {
  if (!column) return "";
  const map = summaryMap || createSummaryMap(instance);
  let rawValue = map.get(column.id);
  if ((rawValue === undefined || rawValue === null || rawValue === "") && column.source === "system") {
    if (column.fieldId === "__request__") {
      return instance?.id !== undefined ? `#${instance.id}` : "";
    }
    if (column.fieldId === "__started_at__") {
      return instance?.startedAt ? formatDateTime(instance.startedAt) : "";
    }
    if (column.fieldId === "__status__") {
      return translateStatus(instance?.status, instance?.statusLabel);
    }
  }
  return rawValue ?? "";
};

const formatFilterDateLabel = (value) => {
  if (!value) return "";
  try {
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString("pt-BR");
  } catch (error) {
    return value;
  }
};

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let current = i;
    for (let j = 0; j < 8; j += 1) {
      current = (current & 1) === 1 ? 0xedb88320 ^ (current >>> 1) : current >>> 1;
    }
    table[i] = current >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    const byte = bytes[i];
    crc = CRC32_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const getDosDateTimeParts = () => {
  const now = new Date();
  let year = now.getFullYear();
  if (year < 1980) {
    year = 1980;
  }
  const dosDate =
    ((year - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate();
  const dosTime =
    (now.getHours() << 11) | (now.getMinutes() << 5) | Math.floor(now.getSeconds() / 2);
  return { dosDate, dosTime };
};

const triggerFileDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const escapeHtml = (value) => {
  return value
    .toString()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const escapeXml = (value) => {
  return value
    .toString()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
};

const toColumnLetter = (index) => {
  let current = index;
  let letters = "";
  while (current >= 0) {
    letters = String.fromCharCode((current % 26) + 65) + letters;
    current = Math.floor(current / 26) - 1;
  }
  return letters;
};

const createZipArchive = (files) => {
  const encoder = typeof TextEncoder !== "undefined" ? new TextEncoder() : null;
  if (!encoder) {
    throw new Error("TextEncoder não está disponível no ambiente atual.");
  }
  const { dosDate, dosTime } = getDosDateTimeParts();
  const localParts = [];
  const centralParts = [];
  let localSize = 0;

  const entries = files.map((file) => {
    const nameBytes = encoder.encode(file.name);
    const dataBytes =
      file.binary instanceof Uint8Array ? file.binary : encoder.encode(file.content || "");
    const crc = crc32(dataBytes);
    const size = dataBytes.length;
    const compressedSize = size;

    const localHeader = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(localHeader.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true);
    localView.setUint16(6, 0, true);
    localView.setUint16(8, 0, true);
    localView.setUint16(10, dosTime, true);
    localView.setUint16(12, dosDate, true);
    localView.setUint32(14, crc, true);
    localView.setUint32(18, compressedSize, true);
    localView.setUint32(22, size, true);
    localView.setUint16(26, nameBytes.length, true);
    localView.setUint16(28, 0, true);
    localHeader.set(nameBytes, 30);
    localParts.push(localHeader, dataBytes);

    const localHeaderOffset = localSize;
    localSize += localHeader.length + dataBytes.length;

    return {
      nameBytes,
      dataBytes,
      crc,
      compressedSize,
      size,
      localHeaderOffset,
      dosDate,
      dosTime,
    };
  });

  let centralSize = 0;
  entries.forEach((entry) => {
    const centralHeader = new Uint8Array(46 + entry.nameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint16(8, 0, true);
    centralView.setUint16(10, 0, true);
    centralView.setUint16(12, entry.dosTime, true);
    centralView.setUint16(14, entry.dosDate, true);
    centralView.setUint32(16, entry.crc, true);
    centralView.setUint32(20, entry.compressedSize, true);
    centralView.setUint32(24, entry.size, true);
    centralView.setUint16(28, entry.nameBytes.length, true);
    centralView.setUint16(30, 0, true);
    centralView.setUint16(32, 0, true);
    centralView.setUint16(34, 0, true);
    centralView.setUint16(36, 0, true);
    centralView.setUint32(38, 0, true);
    centralView.setUint32(42, entry.localHeaderOffset, true);
    centralHeader.set(entry.nameBytes, 46);
    centralParts.push(centralHeader);
    centralSize += centralHeader.length;
  });

  const endRecord = new Uint8Array(22);
  const endView = new DataView(endRecord.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(4, 0, true);
  endView.setUint16(6, 0, true);
  endView.setUint16(8, entries.length, true);
  endView.setUint16(10, entries.length, true);
  endView.setUint32(12, centralSize, true);
  endView.setUint32(16, localSize, true);
  endView.setUint16(20, 0, true);

  const totalSize = localSize + centralSize + endRecord.length;
  const output = new Uint8Array(totalSize);
  let offset = 0;
  [...localParts, ...centralParts, endRecord].forEach((part) => {
    output.set(part, offset);
    offset += part.length;
  });

  return output;
};

const normalizeStatusValue = (status) =>
  typeof status === "string" ? status.trim().toUpperCase() : "";

const DEFAULT_FILTERS = Object.freeze({
  status: "all",
  query: "",
  startDate: "",
  endDate: "",
});

const HISTORY_EMPTY_VALUE_LABEL = "Sem valor";

const translateStatus = (status, label) => {
  if (typeof label === "string" && label.trim()) {
    return label.trim();
  }

  const normalized = normalizeStatusValue(status);
  if (!normalized) {
    return "-";
  }

  return STATUS_LABELS[normalized] || (typeof status === "string" && status.trim() ? status : "-");
};

const normalizeProcessIdValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const normalizeOptions = (options) => {
  if (!Array.isArray(options)) return [];
  return options
    .map((option) => {
      if (!option) return null;
      if (typeof option === "string") {
        return { id: option, label: option };
      }
      const id =
        option.id ?? option.value ?? option.key ?? option.code ?? option.label ?? null;
      if (id === null || id === undefined) {
        return null;
      }
      const label =
        option.label ?? option.name ?? option.title ?? option.value ?? String(id);
      return { id, label };
    })
    .filter(Boolean);
};

const normalizeSummaryColumnEntry = (entry) => {
  if (!entry || typeof entry !== "object") {
    return null;
  }
  const rawSource =
    typeof entry.source === "string" ? entry.source.trim().toLowerCase() : "";
  const source =
    rawSource === "metadata"
      ? "metadata"
      : rawSource === "system"
      ? "system"
      : "form";
  const fieldId =
    typeof entry.fieldId === "string" && entry.fieldId.trim()
      ? entry.fieldId.trim()
      : typeof entry.id === "string" && entry.id.includes(":")
      ? entry.id.split(":").slice(1).join(":")
      : typeof entry.id === "string"
      ? entry.id.trim()
      : "";
  if (!fieldId) {
    return null;
  }
  const label =
    typeof entry.label === "string" && entry.label.trim()
      ? entry.label.trim()
      : fieldId;
  const identifier =
    typeof entry.id === "string" && entry.id.trim()
      ? entry.id.trim()
      : `${source}:${fieldId}`;

  return { id: identifier, fieldId, label, source };
};

const normalizeProcessMetadataField = (field) => {
  if (!field || typeof field !== "object") {
    return null;
  }
  const id = typeof field.id === "string" && field.id.trim() ? field.id.trim() : null;
  const label =
    typeof field.label === "string" && field.label.trim()
      ? field.label.trim()
      : id;
  if (!id || !label) {
    return null;
  }
  const type =
    typeof field.type === "string" && field.type.trim().toLowerCase() === "date"
      ? "date"
      : typeof field.type === "string" && field.type.trim().toLowerCase() === "link"
      ? "link"
      : "text";
  const description =
    typeof field.description === "string" && field.description.trim()
      ? field.description.trim()
      : "";

  return { id, label, type, description };
};

const normalizeProcessSummary = (process) => {
  if (!process || typeof process !== "object") {
    return null;
  }

  const summaryColumns = Array.isArray(process.summaryColumns)
    ? process.summaryColumns
        .map((column) => normalizeSummaryColumnEntry(column))
        .filter(Boolean)
    : [];

  const availableSummaryFields = Array.isArray(process.availableSummaryFields)
    ? process.availableSummaryFields
        .map((column) => normalizeSummaryColumnEntry(column))
        .filter(Boolean)
    : [];

  const metadataFields = Array.isArray(process.metadataFields)
    ? process.metadataFields
        .map((field) => normalizeProcessMetadataField(field))
        .filter(Boolean)
    : [];

  const dashboardHighlightField = normalizeSummaryColumnEntry(
    process.dashboardHighlightField,
  );
  const dedicatedVisibleFieldIds = Array.isArray(process.dedicatedVisibleFieldIds)
    ? process.dedicatedVisibleFieldIds
        .map((fieldId) =>
          typeof fieldId === "string" && fieldId.trim() ? fieldId.trim() : null,
        )
        .filter(Boolean)
    : [];

  return {
    ...process,
    summaryColumns,
    availableSummaryFields,
    metadataFields,
    dashboardHighlightField,
    dedicatedVisibleFieldIds,
  };
};

const describeValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  if (value instanceof Date) {
    return value.toLocaleString("pt-BR");
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "";
  }
  if (typeof value === "boolean") {
    return value ? "Sim" : "Não";
  }
  if (typeof value === "string") {
    return value.trim();
  }
  if (Array.isArray(value)) {
    const entries = value
      .map((item) => describeValue(item))
      .filter((item) => item && item.trim());
    const unique = Array.from(new Set(entries));
    return unique.join(", ");
  }
  if (typeof value === "object") {
    const entries = Object.values(value)
      .map((item) => describeValue(item))
      .filter((item) => item && item.trim());
    if (entries.length) {
      const unique = Array.from(new Set(entries));
      return unique.join(", ");
    }
    try {
      const serialized = JSON.stringify(value);
      return serialized === "{}" ? "" : serialized;
    } catch (error) {
      return "";
    }
  }
  return String(value);
};

const formatMetadataValueDisplay = (field, value) => {
  if (!value) {
    return "";
  }
  if (field.type === "text") {
    return typeof value.value === "string" ? value.value.trim() : "";
  }
  if (field.type === "date") {
    if (!value.value) {
      return "";
    }
    const parsed = new Date(value.value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("pt-BR");
    }
    return value.value;
  }
  if (
    value.linkedProcessId &&
    value.linkedInstanceId &&
    value.linkedFieldId
  ) {
    const segments = [];
    if (value.linkedFieldValue) {
      segments.push(value.linkedFieldValue);
    } else if (value.linkedFieldLabel) {
      segments.push(value.linkedFieldLabel);
    }
    if (value.linkedProcessName) {
      segments.push(value.linkedProcessName);
    } else {
      segments.push(`Processo #${value.linkedProcessId}`);
    }
    segments.push(`Solicitação #${value.linkedInstanceId}`);
    return segments.filter(Boolean).join(" • ");
  }
  return "";
};

const findProcessById = (processes, processId) => {
  if (!Array.isArray(processes)) {
    return null;
  }
  const numericId = Number(processId);
  if (!Number.isFinite(numericId)) {
    return null;
  }
  return (
    processes.find((process) => Number(process?.id) === numericId) || null
  );
};

const useEditableData = (record) => {
  const [initialData, setInitialData] = useState({});
  const [editedData, setEditedData] = useState({});
  const [initialMetadata, setInitialMetadata] = useState({});
  const [editedMetadata, setEditedMetadata] = useState({});

  useEffect(() => {
    if (record?.formData && typeof record.formData === "object") {
      const cloned = JSON.parse(JSON.stringify(record.formData));
      setInitialData(cloned);
      setEditedData(cloned);
    } else {
      setInitialData({});
      setEditedData({});
    }

    if (record?.metadata && typeof record.metadata === "object") {
      const clonedMetadata = JSON.parse(JSON.stringify(record.metadata));
      setInitialMetadata(clonedMetadata);
      setEditedMetadata(clonedMetadata);
    } else {
      setInitialMetadata({});
      setEditedMetadata({});
    }
  }, [record?.id, record?.formData, record?.metadata]);

  const hasDataChanges = useMemo(() => {
    return stableStringify(initialData) !== stableStringify(editedData);
  }, [initialData, editedData]);

  const hasMetadataChanges = useMemo(() => {
    return stableStringify(initialMetadata) !== stableStringify(editedMetadata);
  }, [initialMetadata, editedMetadata]);

  return {
    initialData,
    editedData,
    setEditedData,
    initialMetadata,
    editedMetadata,
    setEditedMetadata,
    hasDataChanges,
    hasMetadataChanges,
    hasChanges: hasDataChanges || hasMetadataChanges,
  };
};

const computeFields = (record, valuesOverride = null) => {
  const schema = record?.formSchema;
  const fields = Array.isArray(schema?.fields) ? schema.fields : [];
  const groups = Array.isArray(schema?.groups) ? schema.groups : [];
  const values =
    (valuesOverride && typeof valuesOverride === "object"
      ? valuesOverride
      : record?.formData) || {};

  const groupMap = new Map();
  groups.forEach((group) => {
    if (!group || typeof group !== "object") return;
    const id = typeof group.id === "string" ? group.id : group.id?.toString();
    if (!id) return;
    groupMap.set(id, group);
  });

  const grouped = new Map();
  const hadSchemaFields = fields.length > 0;
  fields.forEach((field) => {
    if (!field || typeof field !== "object") return;
    const id = field.id;
    if (!id) return;
    if (!fieldVisible(field, values)) return;
    const groupId = field.groupId || "__default__";
    if (!grouped.has(groupId)) {
      grouped.set(groupId, []);
    }
    grouped.get(groupId).push(field);
  });

  if (
    !grouped.size &&
    !hadSchemaFields &&
    record?.formData &&
    typeof record.formData === "object"
  ) {
    const extraFields = Object.keys(record.formData).map((key) => ({ id: key }));
    grouped.set("__default__", extraFields);
  }

  const orderedGroups = Array.from(grouped.entries())
    .map(([groupId, groupFields]) => {
      const meta = groupMap.get(groupId);
      const label =
        meta && typeof meta.label === "string" && meta.label.trim()
          ? meta.label.trim()
          : groupId === "__default__"
          ? "Campos"
          : null;
      return {
        id: groupId,
        label,
        fields: groupFields,
      };
    })
    .filter((group) => Array.isArray(group.fields) && group.fields.length > 0);

  return orderedGroups;
};

function RegistrationCenter({
  openTab,
  onCreateRegistrationPage,
  onUpdateRegistrationPage,
  onDeleteRegistrationPage,
  canManageRegistrations = false,
  registrationPages = [],
  initialProcessId = null,
  lockedProcessId = null,
  registrationPageName = "",
  closeTab,
}) {
  const normalizedInitialProcessId = normalizeProcessIdValue(initialProcessId);
  const normalizedLockedProcessId = normalizeProcessIdValue(lockedProcessId);
  const isLockedProcess = normalizedLockedProcessId !== null;
  const { user } = useAuth();
  const [processes, setProcesses] = useState([]);
  const [loadingProcesses, setLoadingProcesses] = useState(false);
  const [processesFetched, setProcessesFetched] = useState(false);
  const [processError, setProcessError] = useState("");
  const [selectedProcessId, setSelectedProcessId] = useState(null);

  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [instancesError, setInstancesError] = useState("");
  const [selectedInstanceId, setSelectedInstanceId] = useState(null);

  const [record, setRecord] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [recordError, setRecordError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [filtersByProcess, setFiltersByProcess] = useState({});
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [iconModalOpen, setIconModalOpen] = useState(false);
  const [iconSelection, setIconSelection] = useState(DEFAULT_REGISTRATION_PAGE_ICON);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [pageActionError, setPageActionError] = useState("");
  const [pageActionSuccess, setPageActionSuccess] = useState("");
  const [settingsSuccess, setSettingsSuccess] = useState("");
  const [settingsError, setSettingsError] = useState("");
  const [highlightSaving, setHighlightSaving] = useState(false);
  const [highlightModalOpen, setHighlightModalOpen] = useState(false);
  const [historyByInstance, setHistoryByInstance] = useState({});
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportExporting, setReportExporting] = useState(false);
  const [reportError, setReportError] = useState("");
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [columnsModalState, setColumnsModalState] = useState({
    open: false,
    draft: [],
    saving: false,
    error: "",
    context: "consultation",
  });
  const [metadataModalState, setMetadataModalState] = useState({
    open: false,
    draft: [],
    saving: false,
    error: "",
  });
  const [dedicatedFieldsModalState, setDedicatedFieldsModalState] = useState({
    open: false,
    saving: false,
    error: "",
    selection: {},
  });
  const [detailsViewTab, setDetailsViewTab] = useState("details");
  const [dashboardSelectedFieldsByProcess, setDashboardSelectedFieldsByProcess] = useState({});
  const [dashboardFieldDraft, setDashboardFieldDraft] = useState("");
  const [dashboardInstancesDataByProcess, setDashboardInstancesDataByProcess] = useState({});
  const [dashboardDataLoading, setDashboardDataLoading] = useState(false);

  const handleOpenFileManager = useCallback(() => {
    if (typeof openTab !== "function") return;
    openTab("file-manager", "Arquivos", FileManager, { openTab });
  }, [openTab]);
  const canOpenFileManager = useMemo(() => typeof openTab === "function", [openTab]);
  const [linkModalState, setLinkModalState] = useState({
    open: false,
    field: null,
    currentValue: null,
    processId: null,
    instanceId: null,
    fieldId: "",
    instances: [],
    instancesLoading: false,
    details: null,
    detailsLoading: false,
    error: "",
    search: "",
  });
  const closeTabRef = useRef(closeTab);

  useEffect(() => {
    closeTabRef.current = closeTab;
  }, [closeTab]);

  useEffect(() => {
    let isMounted = true;
    const loadLogo = async () => {
      try {
        const response = await fetch("/nashlogoazul.png");
        if (!response.ok) {
          throw new Error("Falha ao carregar logo da Nash");
        }
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onload = () => {
          if (isMounted) {
            setLogoDataUrl(reader.result);
          }
        };
        reader.onerror = () => {
          if (isMounted) {
            setLogoDataUrl(null);
          }
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        if (isMounted) {
          setLogoDataUrl(null);
        }
      }
    };
    loadLogo();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(DASHBOARD_FIELD_SELECTION_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        setDashboardSelectedFieldsByProcess(parsed);
      }
    } catch (error) {
      console.error("Erro ao carregar seleção de campos do dashboard", error);
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        DASHBOARD_FIELD_SELECTION_STORAGE_KEY,
        JSON.stringify(dashboardSelectedFieldsByProcess),
      );
    } catch (error) {
      console.error("Erro ao salvar seleção de campos do dashboard", error);
    }
  }, [dashboardSelectedFieldsByProcess]);

  useEffect(() => {
    const loadDashboardFieldData = async () => {
      if (!selectedProcessId || detailsViewTab !== "dashboard") return;
      const selectedFields = dashboardSelectedFieldsByProcess[selectedProcessId] || [];
      if (!selectedFields.length || !instances.length) return;

      const process = processes.find((item) => Number(item?.id) === Number(selectedProcessId));
      const summaryIds = new Set(
        Array.isArray(process?.summaryColumns)
          ? process.summaryColumns.map((column) => column?.id).filter(Boolean)
          : [],
      );
      const fieldsNeedingDetails = selectedFields.filter((fieldId) => !summaryIds.has(fieldId));
      if (!fieldsNeedingDetails.length) return;

      const cached = dashboardInstancesDataByProcess[selectedProcessId] || {};
      const missingInstanceIds = instances
        .map((instance) => instance?.id)
        .filter((id) => id !== undefined && id !== null && !cached[id]);
      if (!missingInstanceIds.length) return;

      setDashboardDataLoading(true);
      try {
        const responses = await Promise.all(
          missingInstanceIds.map(async (instanceId) => {
            const response = await api.get(`/cadastros/${selectedProcessId}/instances/${instanceId}`);
            return { instanceId, data: response.data };
          }),
        );
        setDashboardInstancesDataByProcess((prev) => {
          const current = prev[selectedProcessId] || {};
          const next = { ...current };
          responses.forEach(({ instanceId, data }) => {
            next[instanceId] = data;
          });
          return { ...prev, [selectedProcessId]: next };
        });
      } catch (error) {
        console.error("Erro ao carregar dados detalhados do dashboard", error);
      } finally {
        setDashboardDataLoading(false);
      }
    };

    loadDashboardFieldData();
  }, [
    dashboardInstancesDataByProcess,
    dashboardSelectedFieldsByProcess,
    detailsViewTab,
    instances,
    processes,
    selectedProcessId,
  ]);

  const clearPageActionFeedback = useCallback(() => {
    setPageActionError("");
    setPageActionSuccess("");
  }, []);
  const clearSettingsFeedback = useCallback(() => {
    setSettingsError("");
    setSettingsSuccess("");
  }, []);

  const fetchProcesses = useCallback(async () => {
    setLoadingProcesses(true);
    setProcessesFetched(false);
    setProcessError("");
    try {
      const response = await api.get("/cadastros/processes");
      const list = Array.isArray(response.data) ? response.data : [];
      const normalized = list
        .map((item) => normalizeProcessSummary(item))
        .filter(Boolean);
      setProcesses(normalized);
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Não foi possível carregar os processos disponíveis.";
      setProcessError(message);
      setProcesses([]);
    } finally {
      setLoadingProcesses(false);
      setProcessesFetched(true);
    }
  }, []);

  const fetchInstances = useCallback(
    async (processId) => {
      if (!processId) {
        setInstances([]);
        setInstancesError("");
        return;
      }
      setLoadingInstances(true);
      setInstancesError("");
      try {
        const response = await api.get(`/cadastros/${processId}/instances`);
        const list = Array.isArray(response.data) ? response.data : [];
        setInstances(list);
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          error?.message ||
          "Não foi possível carregar as solicitações desse processo.";
        setInstancesError(message);
        setInstances([]);
      } finally {
        setLoadingInstances(false);
      }
    },
    [],
  );

  const fetchRecord = useCallback(
    async (processId, instanceId) => {
      if (!processId || !instanceId) {
        setRecord(null);
        setRecordError("");
        return;
      }
      setLoadingRecord(true);
      setRecordError("");
      setSaveSuccess("");
      setSaveError("");
      try {
        const response = await api.get(`/cadastros/${processId}/instances/${instanceId}`);
        setRecord(response.data);
        if (processId && instanceId) {
          const key = `${processId}:${instanceId}`;
          const serverHistory = Array.isArray(response.data?.editHistory)
            ? response.data.editHistory
            : [];
          setHistoryByInstance((prev) => ({
            ...prev,
            [key]: serverHistory,
          }));
        }
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          error?.message ||
          "Não foi possível carregar os dados da solicitação.";
        setRecordError(message);
        setRecord(null);
      } finally {
        setLoadingRecord(false);
      }
    },
    [],
  );

  const activeFilters = useMemo(() => {
    if (!selectedProcessId) {
      return DEFAULT_FILTERS;
    }

    return filtersByProcess[selectedProcessId] || DEFAULT_FILTERS;
  }, [filtersByProcess, selectedProcessId]);

  const hasActiveFilters = useMemo(() => {
    if (!selectedProcessId) {
      return false;
    }

    if (activeFilters.status !== DEFAULT_FILTERS.status) {
      return true;
    }

    const query = typeof activeFilters.query === "string" ? activeFilters.query.trim() : "";
    if (query) {
      return true;
    }

    if (activeFilters.startDate || activeFilters.endDate) {
      return true;
    }

    return false;
  }, [activeFilters, selectedProcessId]);

  const updateActiveFilters = useCallback(
    (updates) => {
      if (!selectedProcessId) return;

      setFiltersByProcess((prev) => {
        const current = prev[selectedProcessId] || { ...DEFAULT_FILTERS };
        const next = { ...current, ...updates };

        if (
          prev[selectedProcessId] &&
          stableStringify(prev[selectedProcessId]) === stableStringify(next)
        ) {
          return prev;
        }

        return {
          ...prev,
          [selectedProcessId]: next,
        };
      });
    },
    [selectedProcessId],
  );

  const clearActiveFilters = useCallback(() => {
    if (!selectedProcessId) return;

    setFiltersByProcess((prev) => {
      if (
        prev[selectedProcessId] &&
        stableStringify(prev[selectedProcessId]) ===
          stableStringify(DEFAULT_FILTERS)
      ) {
        return prev;
      }

      return {
        ...prev,
        [selectedProcessId]: { ...DEFAULT_FILTERS },
      };
    });
  }, [selectedProcessId]);

  const handleStatusFilterChange = useCallback(
    (event) => {
      updateActiveFilters({ status: event.target.value || "all" });
    },
    [updateActiveFilters],
  );

  const handleQueryFilterChange = useCallback(
    (event) => {
      updateActiveFilters({ query: event.target.value || "" });
    },
    [updateActiveFilters],
  );

  const handleDateFilterChange = useCallback(
    (field) => (event) => {
      updateActiveFilters({ [field]: event.target.value || "" });
    },
    [updateActiveFilters],
  );

  const {
    initialData,
    editedData,
    setEditedData,
    initialMetadata,
    editedMetadata,
    setEditedMetadata,
    hasChanges,
  } = useEditableData(record);

  const historyKey = useMemo(() => {
    if (!selectedProcessId || !selectedInstanceId) {
      return null;
    }
    return `${selectedProcessId}:${selectedInstanceId}`;
  }, [selectedInstanceId, selectedProcessId]);

  const editHistory = useMemo(() => {
    if (!historyKey) {
      return [];
    }
    const history = historyByInstance[historyKey];
    return Array.isArray(history) ? history : [];
  }, [historyByInstance, historyKey]);

  const applyProcessSettingsUpdate = useCallback((processId, updates) => {
    setProcesses((prev) => {
      const numericId = Number(processId);
      return prev.map((process) => {
        const processNumericId = Number(process?.id);
        if (!Number.isFinite(processNumericId) || processNumericId !== numericId) {
          return process;
        }
        const merged = { ...process, ...updates };
        const normalized = normalizeProcessSummary(merged);
        return normalized || merged;
      });
    });

    setRecord((prev) => {
      if (!prev || Number(prev?.process?.id) !== Number(processId)) {
        return prev;
      }
      return {
        ...prev,
        metadataFields:
          updates.metadataFields !== undefined
            ? updates.metadataFields
            : prev.metadataFields || [],
        dedicatedVisibleFieldIds:
          updates.dedicatedVisibleFieldIds !== undefined
            ? updates.dedicatedVisibleFieldIds
            : prev.dedicatedVisibleFieldIds || [],
      };
    });
  }, []);
  const dedicatedFieldOptions = useMemo(() => {
    const schemaFields = Array.isArray(record?.formSchema?.fields)
      ? record.formSchema.fields
      : [];
    const seen = new Set();
    return schemaFields
      .map((field) => {
        const fieldId =
          typeof field?.id === "string" && field.id.trim() ? field.id.trim() : null;
        if (!fieldId || seen.has(fieldId)) {
          return null;
        }
        seen.add(fieldId);
        return {
          id: fieldId,
          label: resolveFieldLabel(field),
        };
      })
      .filter(Boolean);
  }, [record?.formSchema]);

  const handleOpenColumnsModal = useCallback((context = "consultation") => {
    if (!selectedProcessId) return;
    const targetProcess = findProcessById(processes, selectedProcessId);
    if (!targetProcess) {
      return;
    }
    clearSettingsFeedback();
    setColumnsModalState({
      open: true,
      draft: Array.isArray(targetProcess.summaryColumns)
        ? targetProcess.summaryColumns.map((column) => ({ ...column }))
        : [],
      saving: false,
      error: "",
      context,
    });
  }, [clearSettingsFeedback, processes, selectedProcessId]);

  const handleOpenDedicatedFieldsModal = useCallback(() => {
    if (!selectedProcessId || !record?.formSchema) {
      setDedicatedFieldsModalState({
        open: true,
        saving: false,
        error: "Selecione uma solicitação para carregar os campos da página dedicada.",
        selection: {},
      });
      return;
    }
    if (!dedicatedFieldOptions.length) {
      setDedicatedFieldsModalState({
        open: true,
        saving: false,
        error: "Não encontramos campos configurados no formulário deste processo.",
        selection: {},
      });
      return;
    }
    const targetProcess = findProcessById(processes, selectedProcessId);
    const configuredIds = Array.isArray(targetProcess?.dedicatedVisibleFieldIds)
      ? targetProcess.dedicatedVisibleFieldIds
      : [];
    const configuredSet = new Set(configuredIds);
    const useAllByDefault = configuredSet.size === 0;
    const selection = {};
    dedicatedFieldOptions.forEach((field) => {
      selection[field.id] = useAllByDefault ? true : configuredSet.has(field.id);
    });
    setDedicatedFieldsModalState({
      open: true,
      saving: false,
      error: "",
      selection,
    });
    clearSettingsFeedback();
  }, [clearSettingsFeedback, dedicatedFieldOptions, processes, record?.formSchema, selectedProcessId]);

  const handleCloseDedicatedFieldsModal = useCallback(() => {
    setDedicatedFieldsModalState({
      open: false,
      saving: false,
      error: "",
      selection: {},
    });
  }, []);

  const handleToggleDedicatedField = useCallback((fieldId) => {
    setDedicatedFieldsModalState((prev) => ({
      ...prev,
      selection: {
        ...prev.selection,
        [fieldId]: !prev.selection[fieldId],
      },
    }));
  }, []);

  const handleSaveDedicatedVisibleFields = useCallback(async () => {
    if (!selectedProcessId) return;
    const selectedFieldIds = Object.entries(dedicatedFieldsModalState.selection)
      .filter(([, visible]) => Boolean(visible))
      .map(([fieldId]) => fieldId);

    setDedicatedFieldsModalState((prev) => ({ ...prev, saving: true, error: "" }));
    try {
      const response = await api.put(`/cadastros/${selectedProcessId}/settings`, {
        dedicatedVisibleFieldIds: selectedFieldIds,
      });
      const normalized = normalizeProcessSummary({
        id: selectedProcessId,
        ...response.data,
      });
      const updates = normalized
        ? {
            summaryColumns: normalized.summaryColumns,
            availableSummaryFields: normalized.availableSummaryFields,
            metadataFields: normalized.metadataFields,
            dashboardHighlightField: normalized.dashboardHighlightField || null,
            dedicatedVisibleFieldIds: normalized.dedicatedVisibleFieldIds || [],
          }
        : {
            dedicatedVisibleFieldIds: response.data?.dedicatedVisibleFieldIds || [],
          };
      applyProcessSettingsUpdate(selectedProcessId, updates);
      setSettingsSuccess("Campos visíveis da página dedicada atualizados com sucesso.");
      setSettingsError("");
      setDedicatedFieldsModalState({
        open: false,
        saving: false,
        error: "",
        selection: {},
      });
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Não foi possível salvar os campos visíveis da página dedicada.";
      setDedicatedFieldsModalState((prev) => ({ ...prev, error: message }));
    } finally {
      setDedicatedFieldsModalState((prev) => ({ ...prev, saving: false }));
    }
  }, [applyProcessSettingsUpdate, dedicatedFieldsModalState.selection, selectedProcessId]);

  const handleCloseColumnsModal = useCallback(() => {
    setColumnsModalState((prev) => ({ ...prev, open: false, error: "", draft: [] }));
  }, []);

  const handleAddColumnToDraft = useCallback((column) => {
    setColumnsModalState((prev) => {
      if (prev.draft.some((item) => item.id === column.id)) {
        return prev;
      }
      return {
        ...prev,
        draft: [...prev.draft, { ...column }],
        error: "",
      };
    });
  }, []);

  const handleRemoveColumnFromDraft = useCallback((columnId) => {
    setColumnsModalState((prev) => ({
      ...prev,
      draft: prev.draft.filter((item) => item.id !== columnId),
    }));
  }, []);

  const handleUpdateColumnLabel = useCallback((columnId, label) => {
    setColumnsModalState((prev) => ({
      ...prev,
      draft: prev.draft.map((item) =>
        item.id === columnId ? { ...item, label } : item,
      ),
    }));
  }, []);

  const handleMoveColumnInDraft = useCallback((columnId, direction) => {
    setColumnsModalState((prev) => {
      const index = prev.draft.findIndex((item) => item.id === columnId);
      if (index === -1) {
        return prev;
      }
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= prev.draft.length) {
        return prev;
      }
      const nextDraft = [...prev.draft];
      const [moved] = nextDraft.splice(index, 1);
      nextDraft.splice(targetIndex, 0, moved);
      return { ...prev, draft: nextDraft };
    });
  }, []);

  const handleSaveColumns = useCallback(async () => {
    if (!selectedProcessId) {
      return;
    }
    if (!columnsModalState.draft.length) {
      setColumnsModalState((prev) => ({
        ...prev,
        error: "Selecione ao menos um campo para exibir no quadro de consultas.",
      }));
      return;
    }

    setColumnsModalState((prev) => ({ ...prev, saving: true, error: "" }));
    try {
      const targetProcess = findProcessById(processes, selectedProcessId);
      const metadataFields = Array.isArray(targetProcess?.metadataFields)
        ? targetProcess.metadataFields
        : [];
      const payload = {
        summaryColumns: columnsModalState.draft.map((column) => ({
          id: column.id,
          fieldId: column.fieldId,
          source: column.source,
          label: column.label,
        })),
        metadataFields,
      };
      const response = await api.put(
        `/cadastros/${selectedProcessId}/settings`,
        payload,
      );
      const normalized = normalizeProcessSummary({
        id: selectedProcessId,
        ...response.data,
      });
      const updates = normalized
        ? {
            summaryColumns: normalized.summaryColumns,
            availableSummaryFields: normalized.availableSummaryFields,
            metadataFields: normalized.metadataFields,
            dashboardHighlightField:
              normalized.dashboardHighlightField || null,
          }
        : {
            summaryColumns: response.data.summaryColumns || [],
            availableSummaryFields: response.data.availableSummaryFields || [],
            metadataFields: response.data.metadataFields || [],
            dashboardHighlightField:
              response.data.dashboardHighlightField || null,
          };
      applyProcessSettingsUpdate(selectedProcessId, updates);
      setSettingsSuccess(
        "Colunas do quadro de consultas atualizadas com sucesso.",
      );
      setSettingsError("");
      setColumnsModalState({
        open: false,
        draft: [],
        saving: false,
        error: "",
        context: "consultation",
      });
      fetchInstances(selectedProcessId);
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Não foi possível salvar as colunas selecionadas.";
      setColumnsModalState((prev) => ({ ...prev, error: message }));
    } finally {
      setColumnsModalState((prev) => ({ ...prev, saving: false }));
    }
  }, [
    applyProcessSettingsUpdate,
    columnsModalState.draft,
    fetchInstances,
    processes,
    selectedProcessId,
  ]);

  const handleOpenHighlightModal = useCallback(() => {
    if (!selectedProcessId) {
      return;
    }
    clearSettingsFeedback();
    setHighlightModalOpen(true);
  }, [clearSettingsFeedback, selectedProcessId]);

  const handleCloseHighlightModal = useCallback(() => {
    setHighlightModalOpen(false);
  }, []);

  const handleHighlightSelection = useCallback(
    async (event) => {
      if (!selectedProcessId) {
        return;
      }

      const selectedValue = event.target.value;
      clearSettingsFeedback();
      setHighlightSaving(true);
      try {
        const targetProcess = findProcessById(processes, selectedProcessId);
        const availableFields = Array.isArray(targetProcess?.availableSummaryFields)
          ? targetProcess.availableSummaryFields
          : [];
        const selectedField = availableFields.find(
          (field) => field && field.id === selectedValue,
        );

        const payload = {
          dashboardHighlightField: selectedField
            ? {
                id: selectedField.id,
                fieldId: selectedField.fieldId,
                source: selectedField.source,
                label: selectedField.label,
              }
            : null,
        };

        const response = await api.put(
          `/cadastros/${selectedProcessId}/settings`,
          payload,
        );

        const normalized = normalizeProcessSummary({
          id: selectedProcessId,
          ...response.data,
        });

        const updates = normalized
          ? {
              summaryColumns: normalized.summaryColumns,
              availableSummaryFields: normalized.availableSummaryFields,
              metadataFields: normalized.metadataFields,
              dashboardHighlightField:
                normalized.dashboardHighlightField || null,
            }
          : {
              summaryColumns: response.data.summaryColumns || [],
              availableSummaryFields:
                response.data.availableSummaryFields || [],
              metadataFields: response.data.metadataFields || [],
              dashboardHighlightField:
                response.data.dashboardHighlightField || null,
            };

        applyProcessSettingsUpdate(selectedProcessId, updates);
        setSettingsSuccess(
          selectedField
            ? `O campo "${selectedField.label}" será destacado no dashboard.`
            : "Identificador do dashboard removido.",
        );
        setSettingsError("");
        setHighlightModalOpen(false);
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          error?.message ||
          "Não foi possível atualizar o identificador do dashboard.";
        setSettingsError(message);
      } finally {
        setHighlightSaving(false);
      }
    },
    [
      applyProcessSettingsUpdate,
      clearSettingsFeedback,
      processes,
      selectedProcessId,
    ],
  );

  const metadataSuggestions = useMemo(
    () => [
      {
        label: "Nome do solicitante",
        type: "text",
        description: "Identifique rapidamente quem solicitou o cadastro.",
      },
      {
        label: "Data da solicitação",
        type: "date",
        description: "Registre quando a demanda foi realizada.",
      },
      {
        label: "Vínculo com outro processo",
        type: "link",
        description: "Associe esta solicitação a um processo relacionado.",
      },
    ],
    [],
  );

  const handleOpenMetadataModal = useCallback(() => {
    if (!selectedProcessId) return;
    const targetProcess = findProcessById(processes, selectedProcessId);
    if (!targetProcess) {
      return;
    }
    clearSettingsFeedback();
    setMetadataModalState({
      open: true,
      draft: Array.isArray(targetProcess.metadataFields)
        ? targetProcess.metadataFields.map((field) => ({ ...field }))
        : [],
      saving: false,
      error: "",
    });
  }, [clearSettingsFeedback, processes, selectedProcessId]);

  const handleCloseMetadataModal = useCallback(() => {
    setMetadataModalState((prev) => ({ ...prev, open: false, error: "", draft: [] }));
  }, []);

  const handleAddMetadataField = useCallback(() => {
    setMetadataModalState((prev) => ({
      ...prev,
      draft: [
        ...prev.draft,
        {
          id: "",
          label: "",
          type: "text",
          description: "",
        },
      ],
      error: "",
    }));
  }, []);

  const handleAddMetadataSuggestion = useCallback((suggestion) => {
    setMetadataModalState((prev) => {
      const alreadyExists = prev.draft.some(
        (field) =>
          typeof field.label === "string" &&
          field.label.trim().toLowerCase() === suggestion.label.toLowerCase(),
      );
      if (alreadyExists) {
        return prev;
      }
      return {
        ...prev,
        draft: [
          ...prev.draft,
          {
            id: "",
            label: suggestion.label,
            type: suggestion.type,
            description: suggestion.description || "",
          },
        ],
        error: "",
      };
    });
  }, []);

  const handleUpdateMetadataDraft = useCallback((index, updates) => {
    setMetadataModalState((prev) => {
      if (index < 0 || index >= prev.draft.length) {
        return prev;
      }
      const nextDraft = [...prev.draft];
      nextDraft[index] = { ...nextDraft[index], ...updates };
      return { ...prev, draft: nextDraft };
    });
  }, []);

  const handleRemoveMetadataField = useCallback((index) => {
    setMetadataModalState((prev) => ({
      ...prev,
      draft: prev.draft.filter((_, itemIndex) => itemIndex !== index),
    }));
  }, []);

  const handleSaveMetadataFields = useCallback(async () => {
    if (!selectedProcessId) {
      return;
    }

    if (
      metadataModalState.draft.some(
        (field) => !field || !field.label || !field.label.trim(),
      )
    ) {
      setMetadataModalState((prev) => ({
        ...prev,
        error: "Informe um nome para todos os campos extras.",
      }));
      return;
    }

    const normalizedLabels = new Set();
    for (const field of metadataModalState.draft) {
      const normalized = field.label.trim().toLowerCase();
      if (normalizedLabels.has(normalized)) {
        setMetadataModalState((prev) => ({
          ...prev,
          error: "Existem campos extras com nomes duplicados.",
        }));
        return;
      }
      normalizedLabels.add(normalized);
    }

    setMetadataModalState((prev) => ({ ...prev, saving: true, error: "" }));
    try {
      const targetProcess = findProcessById(processes, selectedProcessId);
      const summaryColumns = Array.isArray(targetProcess?.summaryColumns)
        ? targetProcess.summaryColumns
        : [];
      const payload = {
        metadataFields: metadataModalState.draft.map((field) => ({
          id: field.id,
          label: field.label,
          type: field.type,
          description: field.description,
        })),
        summaryColumns,
      };
      const response = await api.put(
        `/cadastros/${selectedProcessId}/settings`,
        payload,
      );
      const normalized = normalizeProcessSummary({
        id: selectedProcessId,
        ...response.data,
      });
      const updates = normalized
        ? {
            summaryColumns: normalized.summaryColumns,
            availableSummaryFields: normalized.availableSummaryFields,
            metadataFields: normalized.metadataFields,
            dashboardHighlightField:
              normalized.dashboardHighlightField || null,
          }
        : {
            summaryColumns: response.data.summaryColumns || [],
            availableSummaryFields: response.data.availableSummaryFields || [],
            metadataFields: response.data.metadataFields || [],
            dashboardHighlightField:
              response.data.dashboardHighlightField || null,
          };
      applyProcessSettingsUpdate(selectedProcessId, updates);
      setSettingsSuccess("Campos extras configurados com sucesso.");
      setSettingsError("");
      setMetadataModalState({ open: false, draft: [], saving: false, error: "" });
      if (selectedInstanceId) {
        fetchRecord(selectedProcessId, selectedInstanceId);
      }
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Não foi possível salvar os campos extras.";
      setMetadataModalState((prev) => ({ ...prev, error: message }));
    } finally {
      setMetadataModalState((prev) => ({ ...prev, saving: false }));
    }
  }, [
    applyProcessSettingsUpdate,
    fetchRecord,
    metadataModalState.draft,
    processes,
    selectedInstanceId,
    selectedProcessId,
  ]);

  const fetchLinkInstanceDetails = useCallback(
    async (processId, instanceId) => {
      if (!processId || !instanceId) {
        return;
      }
      setLinkModalState((prev) => ({
        ...prev,
        processId,
        instanceId,
        detailsLoading: true,
        error: "",
      }));
      try {
        const response = await api.get(
          `/cadastros/${processId}/instances/${instanceId}`,
        );
        setLinkModalState((prev) => ({
          ...prev,
          processId,
          instanceId,
          details: response.data,
          detailsLoading: false,
        }));
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          error?.message ||
          "Não foi possível carregar os dados do processo vinculado.";
        setLinkModalState((prev) => ({
          ...prev,
          details: null,
          detailsLoading: false,
          error: message,
        }));
      }
    },
    [],
  );

  const fetchLinkInstances = useCallback(
    async (processId, preloadInstanceId = null) => {
      if (!processId) {
        return;
      }
      setLinkModalState((prev) => ({
        ...prev,
        processId,
        instanceId: preloadInstanceId,
        instancesLoading: true,
        instances: [],
        details: null,
        search: "",
        error: "",
      }));
      try {
        const response = await api.get(`/cadastros/${processId}/instances`);
        const list = Array.isArray(response.data) ? response.data : [];
        setLinkModalState((prev) => ({
          ...prev,
          processId,
          instances: list,
          instancesLoading: false,
        }));
        if (preloadInstanceId) {
          await fetchLinkInstanceDetails(processId, preloadInstanceId);
        }
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          error?.message ||
          "Não foi possível listar as solicitações disponíveis.";
        setLinkModalState((prev) => ({
          ...prev,
          processId,
          instances: [],
          instancesLoading: false,
          error: message,
        }));
      }
    },
    [fetchLinkInstanceDetails],
  );

  const handleOpenLinkModal = useCallback(
    (field) => {
      if (!field) return;
      clearSettingsFeedback();
      const currentValue = editedMetadata?.[field.id] || null;
      const initialProcessId =
        currentValue && typeof currentValue.linkedProcessId === "number"
          ? currentValue.linkedProcessId
          : null;
      const initialInstanceId =
        currentValue && typeof currentValue.linkedInstanceId === "number"
          ? currentValue.linkedInstanceId
          : null;
      setLinkModalState({
        open: true,
        field,
        currentValue,
        processId: initialProcessId,
        instanceId: initialInstanceId,
        fieldId:
          currentValue && typeof currentValue.linkedFieldId === "string"
            ? currentValue.linkedFieldId
            : "",
        instances: [],
        instancesLoading: false,
        details: null,
        detailsLoading: false,
        error: "",
        search: "",
      });
      if (initialProcessId) {
        fetchLinkInstances(initialProcessId, initialInstanceId);
      }
    },
    [clearSettingsFeedback, editedMetadata, fetchLinkInstances],
  );

  const handleCloseLinkModal = useCallback(() => {
    setLinkModalState((prev) => ({
      ...prev,
      open: false,
      error: "",
      instances: [],
      details: null,
    }));
  }, []);

  const handleSelectLinkProcess = useCallback(
    (processId) => {
      if (!processId) {
        setLinkModalState((prev) => ({
          ...prev,
          processId: null,
          instanceId: null,
          fieldId: "",
          instances: [],
          details: null,
          search: "",
          error: "",
        }));
        return;
      }
      setLinkModalState((prev) => ({
        ...prev,
        processId,
        instanceId: null,
        fieldId: "",
        details: null,
        search: "",
        error: "",
      }));
      fetchLinkInstances(processId);
    },
    [fetchLinkInstances],
  );

  const handleSelectLinkInstance = useCallback(
    (instanceId) => {
      setLinkModalState((prev) => ({
        ...prev,
        instanceId,
        fieldId: "",
        details: null,
        error: "",
      }));
      if (linkModalState.processId && instanceId) {
        fetchLinkInstanceDetails(linkModalState.processId, instanceId);
      }
    },
    [fetchLinkInstanceDetails, linkModalState.processId],
  );

  const handleSelectLinkField = useCallback((fieldId) => {
    setLinkModalState((prev) => ({ ...prev, fieldId }));
  }, []);

  const handleLinkSearchChange = useCallback((value) => {
    setLinkModalState((prev) => ({ ...prev, search: value }));
  }, []);

  const handleMetadataTextChange = useCallback(
    (field, value) => {
      const nextValue = typeof value === "string" ? value : "";
      setEditedMetadata((prev) => ({
        ...prev,
        [field.id]: nextValue.trim()
          ? { type: "text", value: nextValue }
          : null,
      }));
    },
    [setEditedMetadata],
  );

  const handleMetadataDateChange = useCallback(
    (field, value) => {
      const normalized = typeof value === "string" ? value : "";
      setEditedMetadata((prev) => ({
        ...prev,
        [field.id]: normalized ? { type: "date", value: normalized } : null,
      }));
    },
    [setEditedMetadata],
  );

  const handleClearMetadataField = useCallback(
    (fieldId) => {
      setEditedMetadata((prev) => ({ ...prev, [fieldId]: null }));
    },
    [setEditedMetadata],
  );

  const handleConfirmLinkSelection = useCallback(() => {
    const { field, processId, instanceId, fieldId, details } = linkModalState;
    if (!field || !processId || !instanceId || !fieldId || !details) {
      setLinkModalState((prev) => ({
        ...prev,
        error: "Selecione o processo, a solicitação e o campo que deseja vincular.",
      }));
      return;
    }

    const targetProcess = processes.find(
      (item) => Number(item?.id) === Number(processId),
    );
    const availableFields = Array.isArray(targetProcess?.availableSummaryFields)
      ? targetProcess.availableSummaryFields
      : [];
    const selectedField = availableFields.find((entry) => entry.id === fieldId);
    if (!selectedField) {
      setLinkModalState((prev) => ({
        ...prev,
        error: "Campo selecionado não está disponível para este processo.",
      }));
      return;
    }

    let linkedFieldValue = "";
    if (selectedField.source === "form") {
      linkedFieldValue = describeValue(details.formData?.[selectedField.fieldId]);
    } else if (selectedField.source === "metadata") {
      const metaDef = Array.isArray(details.metadataFields)
        ? details.metadataFields.find((item) => item.id === selectedField.fieldId)
        : null;
      const metaValue = details.metadata?.[selectedField.fieldId] || null;
      linkedFieldValue = metaDef
        ? formatMetadataValueDisplay(metaDef, metaValue)
        : "";
    } else if (selectedField.source === "system") {
      if (selectedField.fieldId === "__request__") {
        linkedFieldValue = `#${instanceId}`;
      } else if (selectedField.fieldId === "__status__") {
        linkedFieldValue = translateStatus(details.status, details.statusLabel);
      } else if (selectedField.fieldId === "__started_at__") {
        linkedFieldValue = formatDateTime(details.startedAt);
      }
    }

    const processName =
      (typeof targetProcess?.name === "string" && targetProcess.name) || null;

    setEditedMetadata((prev) => ({
      ...prev,
      [field.id]: {
        type: "link",
        linkedProcessId: processId,
        linkedProcessName: processName,
        linkedInstanceId: instanceId,
        linkedFieldId: selectedField.fieldId,
        linkedFieldLabel: selectedField.label,
        linkedFieldValue: linkedFieldValue || null,
      },
    }));
    setLinkModalState((prev) => ({ ...prev, open: false }));
  }, [linkModalState, processes, setEditedMetadata]);

  const applySelectedProcess = useCallback(
    (processId) => {
      const normalizedId = normalizeProcessIdValue(processId);
      setSelectedProcessId(normalizedId);
      setSelectedInstanceId(null);
      setRecord(null);
      setRecordError("");
      setInstances([]);
      setSaveSuccess("");
      setSaveError("");

      if (normalizedId !== null) {
        setFiltersByProcess((prev) => {
          if (prev[normalizedId]) {
            return prev;
          }
          return {
            ...prev,
            [normalizedId]: { ...DEFAULT_FILTERS },
          };
        });
      }
    },
    [],
  );

  useEffect(() => {
    fetchProcesses();
  }, [fetchProcesses]);

  useEffect(() => {
    if (
      normalizedLockedProcessId !== null &&
      normalizedLockedProcessId !== selectedProcessId
    ) {
      applySelectedProcess(normalizedLockedProcessId);
      return;
    }

    if (
      normalizedLockedProcessId === null &&
      normalizedInitialProcessId !== null &&
      normalizedInitialProcessId !== selectedProcessId
    ) {
      applySelectedProcess(normalizedInitialProcessId);
    }
  }, [
    applySelectedProcess,
    normalizedInitialProcessId,
    normalizedLockedProcessId,
    selectedProcessId,
  ]);

  useEffect(() => {
    if (
      !processesFetched ||
      loadingProcesses ||
      selectedProcessId !== null ||
      normalizedLockedProcessId !== null
    ) {
      return;
    }

    if (normalizedInitialProcessId !== null) {
      applySelectedProcess(normalizedInitialProcessId);
      return;
    }

    const firstProcessId = processes.length ? processes[0]?.id ?? null : null;
    if (firstProcessId !== null && firstProcessId !== undefined) {
      applySelectedProcess(firstProcessId);
    }
  }, [
    applySelectedProcess,
    loadingProcesses,
    normalizedInitialProcessId,
    normalizedLockedProcessId,
    processes,
    processesFetched,
    selectedProcessId,
  ]);

  useEffect(() => {
    if (!selectedProcessId) return;
    fetchInstances(selectedProcessId);
  }, [selectedProcessId, fetchInstances]);

  useEffect(() => {
    if (!selectedProcessId || !selectedInstanceId) return;
    fetchRecord(selectedProcessId, selectedInstanceId);
  }, [selectedProcessId, selectedInstanceId, fetchRecord]);

  useEffect(() => {
    if (!processesFetched || loadingProcesses || processError) {
      return;
    }

    const availableProcessIds = new Set(
      processes
        .map((process) => Number(process?.id))
        .filter((id) => Number.isFinite(id) && id > 0),
    );

    let selectionCleared = false;
    setSelectedProcessId((current) => {
      if (current === null || current === undefined) {
        return current;
      }
      const numericId = Number(current);
      if (!Number.isFinite(numericId) || availableProcessIds.has(numericId)) {
        return current;
      }
      selectionCleared = true;
      return null;
    });

    if (selectionCleared) {
      setSelectedInstanceId(null);
      setRecord(null);
      setInstances([]);
    }

    if (
      !Array.isArray(registrationPages) ||
      registrationPages.length === 0 ||
      typeof onDeleteRegistrationPage !== "function"
    ) {
      return;
    }

    const removedPages = registrationPages.filter((page) => {
      const pageProcessId = Number(page?.processId);
      return Number.isFinite(pageProcessId) && !availableProcessIds.has(pageProcessId);
    });

    if (!removedPages.length) {
      return;
    }

    removedPages.forEach((page) => {
      const pageProcessId = Number(page?.processId);
      if (Number.isFinite(pageProcessId)) {
        onDeleteRegistrationPage(pageProcessId);
      }
    });

    const removedCurrentLocked =
      isLockedProcess &&
      normalizedLockedProcessId !== null &&
      removedPages.some(
        (page) => Number(page?.processId) === normalizedLockedProcessId,
      );

    if (removedCurrentLocked) {
      setPageActionError(
        "O processo vinculado a esta página dedicada não está mais disponível. A página foi removida do menu.",
      );
      setPageActionSuccess("");
      if (closeTabRef.current) {
        closeTabRef.current({ activateTabId: "registration-center" });
      }
    } else if (!isLockedProcess) {
      setPageActionError("");
      setPageActionSuccess(
        removedPages.length === 1
          ? "Removemos automaticamente a página dedicada de um processo que não está mais disponível."
          : "Removemos automaticamente páginas dedicadas de processos que não estão mais disponíveis.",
      );
    }
  }, [
    isLockedProcess,
    loadingProcesses,
    normalizedLockedProcessId,
    onDeleteRegistrationPage,
    processError,
    processes,
    processesFetched,
    registrationPages,
  ]);

  const selectedProcess = useMemo(() => {
    if (selectedProcessId === null || selectedProcessId === undefined) {
      return null;
    }

    const numericId = Number(selectedProcessId);
    if (!Number.isFinite(numericId)) {
      return null;
    }
    return (
      processes.find((process) => {
        const processId = Number(process?.id);
        return Number.isFinite(processId) && processId === numericId;
      }) || null
    );
  }, [processes, selectedProcessId]);

  const processOptions = useMemo(() => {
    return [...processes].sort((a, b) => {
      const nameA = typeof a?.name === "string" ? a.name : "";
      const nameB = typeof b?.name === "string" ? b.name : "";
      return nameA.localeCompare(nameB, "pt-BR");
    });
  }, [processes]);

  const selectedProcessMetadataFields = useMemo(() => {
    return Array.isArray(selectedProcess?.metadataFields)
      ? selectedProcess.metadataFields
      : [];
  }, [selectedProcess]);

  const selectedProcessAvailableFields = useMemo(() => {
    return Array.isArray(selectedProcess?.availableSummaryFields)
      ? selectedProcess.availableSummaryFields
      : [];
  }, [selectedProcess]);

  const selectedProcessHighlightField = useMemo(() => {
    if (!selectedProcess || typeof selectedProcess !== "object") {
      return null;
    }
    const highlight = selectedProcess.dashboardHighlightField;
    if (!highlight || typeof highlight !== "object") {
      return null;
    }
    if (!highlight.id || !highlight.fieldId) {
      return null;
    }
    return highlight;
  }, [selectedProcess]);

  const highlightOptions = useMemo(() => {
    return selectedProcessAvailableFields.filter(
      (field) => field && field.source === "form",
    );
  }, [selectedProcessAvailableFields]);

  const availableColumnsBySource = useMemo(() => {
    if (!Array.isArray(selectedProcessAvailableFields)) {
      return {};
    }
    const selectedIds = new Set(
      columnsModalState.draft.map((item) => item.id),
    );
    const grouped = {};
    selectedProcessAvailableFields.forEach((field) => {
      if (!field || selectedIds.has(field.id)) {
        return;
      }
      const source = field.source || "form";
      if (!grouped[source]) {
        grouped[source] = [];
      }
      grouped[source].push(field);
    });
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => {
        const labelA =
          typeof a.label === "string" && a.label.trim()
            ? a.label.trim()
            : a.fieldId || "";
        const labelB =
          typeof b.label === "string" && b.label.trim()
            ? b.label.trim()
            : b.fieldId || "";
        return labelA.localeCompare(labelB, "pt-BR");
      });
    });
    return grouped;
  }, [columnsModalState.draft, selectedProcessAvailableFields]);

  const linkModalSelectedProcess = useMemo(() => {
    if (!linkModalState.processId) {
      return null;
    }
    const numericId = Number(linkModalState.processId);
    if (!Number.isFinite(numericId)) {
      return null;
    }
    return (
      processes.find((process) => Number(process?.id) === numericId) || null
    );
  }, [linkModalState.processId, processes]);

  const linkModalAvailableFields = useMemo(() => {
    if (!linkModalSelectedProcess) {
      return [];
    }
    return Array.isArray(linkModalSelectedProcess.availableSummaryFields)
      ? linkModalSelectedProcess.availableSummaryFields
      : [];
  }, [linkModalSelectedProcess]);

  const filteredLinkInstances = useMemo(() => {
    if (!Array.isArray(linkModalState.instances)) {
      return [];
    }
    const query =
      typeof linkModalState.search === "string"
        ? linkModalState.search.trim().toLowerCase()
        : "";
    if (!query) {
      return linkModalState.instances;
    }
    return linkModalState.instances.filter((instance) => {
      if (!instance) return false;
      const idMatch = String(instance.id || "").includes(query);
      if (idMatch) {
        return true;
      }
      const statusMatch =
        typeof instance.statusLabel === "string" &&
        instance.statusLabel.toLowerCase().includes(query);
      if (statusMatch) {
        return true;
      }
      const summaryEntries = Array.isArray(instance.formSummary)
        ? instance.formSummary
        : [];
      return summaryEntries.some((entry) => {
        if (!entry) return false;
        if (
          typeof entry.label === "string" &&
          entry.label.toLowerCase().includes(query)
        ) {
          return true;
        }
        if (
          typeof entry.value === "string" &&
          entry.value.toLowerCase().includes(query)
        ) {
          return true;
        }
        return false;
      });
    });
  }, [linkModalState.instances, linkModalState.search]);

  const linkModalDetails = linkModalState.details;
  const linkModalSelectedField = useMemo(() => {
    if (!linkModalState.fieldId) {
      return null;
    }
    return (
      linkModalAvailableFields.find(
        (field) => field.id === linkModalState.fieldId,
      ) || null
    );
  }, [linkModalAvailableFields, linkModalState.fieldId]);

  const linkModalFieldPreview = useMemo(() => {
    if (!linkModalSelectedField || !linkModalDetails) {
      return "";
    }
    if (linkModalSelectedField.source === "form") {
      return describeValue(
        linkModalDetails.formData?.[linkModalSelectedField.fieldId],
      );
    }
    if (linkModalSelectedField.source === "metadata") {
      const metaDef = Array.isArray(linkModalDetails.metadataFields)
        ? linkModalDetails.metadataFields.find(
            (item) => item.id === linkModalSelectedField.fieldId,
          )
        : null;
      const metaValue =
        linkModalDetails.metadata?.[linkModalSelectedField.fieldId] || null;
      return metaDef ? formatMetadataValueDisplay(metaDef, metaValue) : "";
    }
    if (linkModalSelectedField.source === "system") {
      if (linkModalSelectedField.fieldId === "__request__") {
        return `#${linkModalDetails.id}`;
      }
      if (linkModalSelectedField.fieldId === "__status__") {
        return translateStatus(
          linkModalDetails.status,
          linkModalDetails.statusLabel,
        );
      }
      if (linkModalSelectedField.fieldId === "__started_at__") {
        return formatDateTime(linkModalDetails.startedAt);
      }
    }
    return "";
  }, [linkModalDetails, linkModalSelectedField]);

  const linkModalCurrentDisplay = useMemo(() => {
    if (!linkModalState.field || !linkModalState.currentValue) {
      return "";
    }
    return formatMetadataValueDisplay(
      linkModalState.field,
      linkModalState.currentValue,
    );
  }, [linkModalState.currentValue, linkModalState.field]);

  const activeMetadataFields = useMemo(() => {
    if (selectedProcessMetadataFields.length) {
      return selectedProcessMetadataFields;
    }
    return Array.isArray(record?.metadataFields) ? record.metadataFields : [];
  }, [record?.metadataFields, selectedProcessMetadataFields]);

  const metadataFieldDefinitions = useMemo(() => {
    const map = new Map();
    activeMetadataFields.forEach((field) => {
      if (!field || !field.id || map.has(field.id)) {
        return;
      }
      map.set(field.id, field);
    });
    return map;
  }, [activeMetadataFields]);

  const dedicatedPage = useMemo(() => {
    if (!isLockedProcess) {
      return null;
    }
    return (
      registrationPages.find(
        (page) => Number(page?.processId) === normalizedLockedProcessId,
      ) || null
    );
  }, [isLockedProcess, registrationPages, normalizedLockedProcessId]);

  const hasExistingPage = useMemo(() => {
    if (!Array.isArray(registrationPages) || selectedProcessId === null) {
      return false;
    }

    const numericId = Number(selectedProcessId);
    if (!Number.isFinite(numericId)) {
      return false;
    }

    return registrationPages.some(
      (page) => Number(page?.processId) === numericId,
    );
  }, [registrationPages, selectedProcessId]);

  const lockedPageName = useMemo(() => {
    if (!isLockedProcess) {
      return "";
    }

    if (dedicatedPage && typeof dedicatedPage.label === "string") {
      const trimmed = dedicatedPage.label.trim();
      if (trimmed) {
        return trimmed;
      }
    }

    if (typeof registrationPageName === "string" && registrationPageName.trim()) {
      return registrationPageName.trim();
    }

    if (selectedProcess && typeof selectedProcess.name === "string") {
      const trimmed = selectedProcess.name.trim();
      if (trimmed) {
        return trimmed;
      }
    }

    if (normalizedLockedProcessId !== null) {
      return `Processo ${normalizedLockedProcessId}`;
    }

    return "";
  }, [
    dedicatedPage,
    isLockedProcess,
    normalizedLockedProcessId,
    registrationPageName,
    selectedProcess,
  ]);

  const headerTitle = useMemo(() => {
    if (isLockedProcess) {
      if (typeof selectedProcess?.name === "string") {
        const trimmed = selectedProcess.name.trim();
        if (trimmed) {
          return trimmed;
        }
      }

      if (lockedPageName) {
        return lockedPageName;
      }

      return "Processo";
    }

    return "Central de Cadastros";
  }, [isLockedProcess, lockedPageName, selectedProcess]);

  const headerSubtitle = useMemo(() => {
    if (isLockedProcess) {
      return "Visualize e acompanhe as solicitações deste processo dedicado.";
    }

    return "Gerencie cadastros e mantenha os dados dos processos sempre atualizados em um único lugar.";
  }, [isLockedProcess]);
  const dedicatedPageLabel = dedicatedPage?.label || "";
  const currentIconName = dedicatedPage?.icon || DEFAULT_REGISTRATION_PAGE_ICON;
  const DedicatedMenuIcon = useMemo(
    () => resolveRegistrationPageIcon(currentIconName),
    [currentIconName],
  );
  const dedicatedProcessId = dedicatedPage?.processId ?? null;

  useEffect(() => {
    if (renameModalOpen) {
      setRenameValue(dedicatedPageLabel);
    }
  }, [renameModalOpen, dedicatedPageLabel]);

  useEffect(() => {
    if (iconModalOpen) {
      setIconSelection(currentIconName);
    }
  }, [iconModalOpen, currentIconName]);

  useEffect(() => {
    clearPageActionFeedback();
  }, [clearPageActionFeedback, dedicatedProcessId]);

  useEffect(() => {
    clearSettingsFeedback();
  }, [clearSettingsFeedback, selectedProcessId]);

  const canAddProcessPage = Boolean(
    selectedProcessId &&
      !hasExistingPage &&
      typeof onCreateRegistrationPage === "function" &&
      canManageRegistrations,
  );

  const groupedFields = useMemo(
    () => computeFields(record, editedData),
    [record, editedData],
  );
  const dedicatedVisibleFieldIds = useMemo(() => {
    if (!Array.isArray(selectedProcess?.dedicatedVisibleFieldIds)) {
      return [];
    }
    return selectedProcess.dedicatedVisibleFieldIds;
  }, [selectedProcess]);
  const visibleDedicatedFieldSet = useMemo(() => {
    if (!isLockedProcess) {
      return null;
    }
    return new Set(dedicatedVisibleFieldIds);
  }, [dedicatedVisibleFieldIds, isLockedProcess]);
  const displayedGroupedFields = useMemo(() => {
    if (!visibleDedicatedFieldSet) {
      return groupedFields;
    }
    return groupedFields
      .map((group) => ({
        ...group,
        fields: Array.isArray(group.fields)
          ? group.fields.filter((field) => visibleDedicatedFieldSet.has(field.id))
          : [],
      }))
      .filter((group) => Array.isArray(group.fields) && group.fields.length > 0);
  }, [groupedFields, visibleDedicatedFieldSet]);

  const dataFieldDefinitions = useMemo(() => {
    const map = new Map();
    groupedFields.forEach((group) => {
      if (!group || !Array.isArray(group.fields)) {
        return;
      }
      group.fields.forEach((field) => {
        if (!field || !field.id || map.has(field.id)) {
          return;
        }
        map.set(field.id, field);
      });
    });
    return map;
  }, [groupedFields]);

  const summaryColumns = useMemo(() => {
    if (
      Array.isArray(selectedProcess?.summaryColumns) &&
      selectedProcess.summaryColumns.length
    ) {
      return selectedProcess.summaryColumns;
    }

    const instanceWithSummary = instances.find(
      (item) => Array.isArray(item?.formSummary) && item.formSummary.length,
    );

    if (instanceWithSummary) {
      return instanceWithSummary.formSummary
        .map((entry) => {
          if (!entry || typeof entry.id !== "string") {
            return null;
          }
          const source = entry.id.startsWith("metadata:")
            ? "metadata"
            : entry.id.startsWith("system:")
            ? "system"
            : "form";
          const fieldId = entry.id.includes(":")
            ? entry.id.split(":").slice(1).join(":")
            : entry.id;
          const label =
            (typeof entry.label === "string" && entry.label.trim()) || fieldId;
          return { id: entry.id, fieldId, label, source };
        })
        .filter(Boolean);
    }

    return [
      {
        id: "system:__request__",
        fieldId: "__request__",
        label: "Solicitação",
        source: "system",
      },
    ];
  }, [instances, selectedProcess]);

  const meetsStatusRequirement = useMemo(() => {
    if (!record) return false;
    if (typeof record.canEdit === "boolean") {
      return record.canEdit;
    }
    const normalized = normalizeStatusValue(record.status);
    return normalized === "COMPLETED";
  }, [record]);

  const canEdit = useMemo(() => {
    if (!record || !hasExistingPage) {
      return false;
    }
    return meetsStatusRequirement;
  }, [hasExistingPage, meetsStatusRequirement, record]);

  const parseFilterDate = useCallback((value, boundary) => {
    if (!value || typeof value !== "string") {
      return null;
    }

    const [year, month, day] = value.split("-").map((part) => Number(part));
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return null;
    }

    if (boundary === "end") {
      return new Date(year, month - 1, day, 23, 59, 59, 999);
    }
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }, []);

  const filteredInstances = useMemo(() => {
    if (!Array.isArray(instances) || instances.length === 0) {
      return [];
    }

    const normalizedStatusFilter = normalizeStatusValue(activeFilters.status);
    const normalizedQuery =
      typeof activeFilters.query === "string"
        ? activeFilters.query.trim().toLowerCase()
        : "";
    const startDateFilter = parseFilterDate(activeFilters.startDate, "start");
    const endDateFilter = parseFilterDate(activeFilters.endDate, "end");

    return instances.filter((instance) => {
      if (!instance) return false;

      const instanceStatus = normalizeStatusValue(instance.status);
      if (normalizedStatusFilter === "ACTIVE" && instanceStatus !== "ACTIVE") {
        return false;
      }

      if (
        normalizedStatusFilter === "COMPLETED" &&
        instanceStatus !== "COMPLETED"
      ) {
        return false;
      }

      const startedAtDate = instance.startedAt ? new Date(instance.startedAt) : null;

      if (
        startDateFilter &&
        (!startedAtDate || Number.isNaN(startedAtDate.getTime()) || startedAtDate < startDateFilter)
      ) {
        return false;
      }

      if (
        endDateFilter &&
        (!startedAtDate || Number.isNaN(startedAtDate.getTime()) || startedAtDate > endDateFilter)
      ) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      const summaryMap = Array.isArray(instance.formSummary)
        ? new Map(
            instance.formSummary
              .filter((entry) => entry && typeof entry.id === "string")
              .map((entry) => [entry.id, entry.value || ""]),
          )
        : new Map();

      const querySources = summaryColumns.map((column) => {
        const mappedValue = summaryMap.get(column.id);
        if (mappedValue) {
          return mappedValue;
        }
        if (column.source === "system") {
          if (column.fieldId === "__request__") {
            return instance.id !== undefined ? `#${instance.id}` : "";
          }
          if (column.fieldId === "__started_at__") {
            return instance.startedAt ? formatDateTime(instance.startedAt) : "";
          }
          if (column.fieldId === "__status__") {
            return translateStatus(instance.status, instance.statusLabel);
          }
        }
        return "";
      });

      querySources.push(translateStatus(instance.status, instance.statusLabel));

      return querySources.some((source) => {
        if (!source) return false;
        return source.toString().toLowerCase().includes(normalizedQuery);
      });
    });
  }, [activeFilters, instances, parseFilterDate, summaryColumns]);

  const reportTitle = useMemo(() => {
    const processName =
      typeof selectedProcess?.name === "string" && selectedProcess.name.trim()
        ? selectedProcess.name.trim()
        : null;
    return processName
      ? `Relatório de consultas • ${processName}`
      : "Relatório de consultas";
  }, [selectedProcess]);

  const reportHeaders = useMemo(() => {
    const headers = summaryColumns.map((column) => {
      const columnLabel =
        typeof column.label === "string" && column.label.trim()
          ? column.label.trim()
          : column.fieldId;
      const sourceLabel = COLUMN_SOURCE_LABELS[column.source] || "Campo";
      const descriptionFieldId =
        column.fieldId === "__request__" ? "solicitação" : column.fieldId;
      return {
        id: column.id,
        fieldId: column.fieldId,
        source: column.source,
        label: columnLabel,
        description: `${sourceLabel} • ${descriptionFieldId}`,
      };
    });

    headers.push({
      id: "system:__status__",
      fieldId: "__status__",
      source: "system",
      label: "Situação",
      description: `${COLUMN_SOURCE_LABELS.system || "Dados do sistema"} • status`,
    });

    headers.push({
      id: "system:__started_at__",
      fieldId: "__started_at__",
      source: "system",
      label: "Início",
      description: `${COLUMN_SOURCE_LABELS.system || "Dados do sistema"} • startedAt`,
    });

    return headers;
  }, [summaryColumns]);

  useEffect(() => {
    if (!isLockedProcess || loadingInstances || !filteredInstances.length) {
      return;
    }

    const hasValidSelection =
      selectedInstanceId !== null &&
      selectedInstanceId !== undefined &&
      filteredInstances.some((instance) => instance?.id === selectedInstanceId);

    if (hasValidSelection) {
      return;
    }

    const lastInstance = filteredInstances[filteredInstances.length - 1];
    if (lastInstance?.id !== undefined && lastInstance?.id !== null) {
      setSelectedInstanceId(lastInstance.id);
    }
  }, [filteredInstances, isLockedProcess, loadingInstances, selectedInstanceId]);


  const reportRows = useMemo(() => {
    if (!filteredInstances.length) {
      return [];
    }

    return filteredInstances.map((instance) => {
      const summaryMap = createSummaryMap(instance);
      return reportHeaders.map((header) => {
        const resolved = resolveInstanceSummaryValue(instance, header, summaryMap);
        if (
          resolved !== undefined &&
          resolved !== null &&
          String(resolved).trim() !== "" &&
          resolved !== "-"
        ) {
          return String(resolved);
        }
        return "-";
      });
    });
  }, [filteredInstances, reportHeaders]);

  const hasReportData = reportRows.length > 0;

  const handleProcessChange = useCallback(
    (event) => {
      if (isLockedProcess) return;
      applySelectedProcess(event.target.value);
    },
    [applySelectedProcess, isLockedProcess],
  );

  const handleInstanceSelect = useCallback((instanceId) => {
    setSelectedInstanceId(instanceId);
  }, []);

  const handleOpenReportModal = useCallback(() => {
    setReportError(
      hasReportData ? "" : "Nenhum registro corresponde aos filtros aplicados no momento.",
    );
    setReportModalOpen(true);
  }, [hasReportData]);

  const handleCloseReportModal = useCallback(() => {
    setReportModalOpen(false);
    setReportError("");
  }, []);

  const ensureReportData = useCallback(() => {
    if (!hasReportData) {
      setReportError("Não há registros filtrados para exportar.");
      return false;
    }
    setReportError("");
    return true;
  }, [hasReportData]);

  const exportMatrix = useMemo(() => {
    const columnCount = Math.max(reportHeaders.length, 1);
    const fillRow = (values) => {
      const row = Array(columnCount).fill("");
      values.forEach((value, index) => {
        if (index < columnCount) {
          row[index] = value ?? "";
        }
      });
      return row;
    };

    const matrix = [fillRow([reportTitle]), fillRow([REPORT_NOTE]), fillRow([])];
    matrix.push(fillRow(reportHeaders.map((header) => header.label)));
    matrix.push(fillRow(reportHeaders.map((header) => header.description)));
    matrix.push(fillRow([]));
    reportRows.forEach((row) => {
      matrix.push(fillRow(row));
    });

    return { matrix, columnCount };
  }, [reportHeaders, reportRows, reportTitle]);

  const handleExportXls = useCallback(() => {
    if (!ensureReportData()) return;
    setReportExporting(true);
    try {
      const { matrix, columnCount } = exportMatrix;
      const tableRows = [];

      matrix.forEach((row, index) => {
        if (index === 0) {
          tableRows.push(
            `<tr><th colspan="${columnCount}" style="background:${NAVY_HEX};color:#f8fafc;font-size:18px;text-align:left;padding:12px 16px;">${escapeHtml(row[0])}</th></tr>`,
          );
          return;
        }
        if (index === 1) {
          tableRows.push(
            `<tr><td colspan="${columnCount}" style="font-size:10px;color:#475569;padding:4px 16px;">${escapeHtml(REPORT_NOTE)}</td></tr>`,
          );
          return;
        }
        if (index === 2 || index === 5) {
          tableRows.push(`<tr><td colspan="${columnCount}" style="height:6px;"></td></tr>`);
          return;
        }
        if (index === 3) {
          const headerCells = row
            .map(
              (cell) =>
                `<th style="background:#e2e8f0;color:${NAVY_HEX};padding:8px 12px;text-align:left;border:1px solid #cbd5f5;font-weight:600;">${escapeHtml(
                  cell,
                )}</th>`,
            )
            .join("");
          tableRows.push(`<tr>${headerCells}</tr>`);
          return;
        }
        if (index === 4) {
          const descriptorCells = row
            .map(
              (cell) =>
                `<td style="background:#f1f5f9;color:#475569;padding:4px 12px;text-transform:uppercase;font-size:9px;letter-spacing:0.08em;border:1px solid #e2e8f0;">${escapeHtml(
                  cell,
                )}</td>`,
            )
            .join("");
          tableRows.push(`<tr>${descriptorCells}</tr>`);
          return;
        }

        const bodyCells = row
          .map(
            (cell) =>
              `<td style="padding:6px 12px;border:1px solid #e2e8f0;color:${NAVY_HEX};">${escapeHtml(
                cell,
              )}</td>`,
          )
          .join("");
        tableRows.push(`<tr>${bodyCells}</tr>`);
      });

      const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${escapeHtml(
        reportTitle,
      )}</title></head><body><table style="border-collapse:collapse;width:100%;font-family:Arial,Helvetica,sans-serif;font-size:12px;">${tableRows.join(
        "",
      )}</table></body></html>`;
      const blob = new Blob(["\ufeff" + html], {
        type: "application/vnd.ms-excel",
      });
      const filename = buildReportFilename(selectedProcess?.name || "relatorio", "xls");
      triggerFileDownload(blob, filename);
    } catch (error) {
      console.error("Falha ao gerar arquivo XLS", error);
      setReportError("Não foi possível gerar o arquivo XLS. Tente novamente.");
    } finally {
      setReportExporting(false);
    }
  }, [ensureReportData, exportMatrix, reportTitle, selectedProcess]);

  const handleExportXlsx = useCallback(() => {
    if (!ensureReportData()) return;
    setReportExporting(true);
    try {
      const { matrix, columnCount } = exportMatrix;
      const lastCellRef = `${toColumnLetter(columnCount - 1)}${matrix.length}`;
      const sheetRowsXml = matrix
        .map((row, rowIndex) => {
          const rowNumber = rowIndex + 1;
          const cells = row
            .map((cell, cellIndex) => {
              if (cell === null || cell === undefined || String(cell) === "") {
                return "";
              }
              const cellRef = `${toColumnLetter(cellIndex)}${rowNumber}`;
              return `<c r="${cellRef}" t="inlineStr"><is><t>${escapeXml(cell)}</t></is></c>`;
            })
            .filter(Boolean)
            .join("");
          return `<row r="${rowNumber}">${cells}</row>`;
        })
        .join("");

      const sheetXml = `<?xml version="1.0" encoding="UTF-8"?>\n<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">\n  <dimension ref="A1:${lastCellRef}"/>\n  <sheetViews><sheetView workbookViewId="0"/></sheetViews>\n  <sheetFormatPr defaultRowHeight="15"/>\n  <sheetData>${sheetRowsXml}</sheetData>\n</worksheet>`;

      const workbookXml = `<?xml version="1.0" encoding="UTF-8"?>\n<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">\n  <fileVersion appName="xl"/>\n  <workbookPr date1904="0"/>\n  <sheets>\n    <sheet name="Relatório" sheetId="1" r:id="rId1"/>\n  </sheets>\n</workbook>`;

      const contentTypesXml = `<?xml version="1.0" encoding="UTF-8"?>\n<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">\n  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>\n  <Default Extension="xml" ContentType="application/xml"/>\n  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>\n  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>\n  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>\n</Types>`;

      const rootRelsXml = `<?xml version="1.0" encoding="UTF-8"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">\n  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>\n</Relationships>`;

      const workbookRelsXml = `<?xml version="1.0" encoding="UTF-8"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">\n  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>\n  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>\n</Relationships>`;

      const stylesXml = `<?xml version="1.0" encoding="UTF-8"?>\n<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">\n  <fonts count="1">\n    <font><name val="Calibri"/><family val="2"/><scheme val="minor"/></font>\n  </fonts>\n  <fills count="1">\n    <fill><patternFill patternType="none"/></fill>\n  </fills>\n  <borders count="1">\n    <border><left/><right/><top/><bottom/><diagonal/></border>\n  </borders>\n  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>\n  <cellXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/></cellXfs>\n  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>\n</styleSheet>`;

      const zipBytes = createZipArchive([
        { name: "[Content_Types].xml", content: contentTypesXml },
        { name: "_rels/.rels", content: rootRelsXml },
        { name: "xl/workbook.xml", content: workbookXml },
        { name: "xl/_rels/workbook.xml.rels", content: workbookRelsXml },
        { name: "xl/styles.xml", content: stylesXml },
        { name: "xl/worksheets/sheet1.xml", content: sheetXml },
      ]);

      const blob = new Blob([zipBytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const filename = buildReportFilename(selectedProcess?.name || "relatorio", "xlsx");
      triggerFileDownload(blob, filename);
    } catch (error) {
      console.error("Falha ao gerar arquivo XLSX", error);
      setReportError("Não foi possível gerar o arquivo XLSX. Tente novamente.");
    } finally {
      setReportExporting(false);
    }
  }, [ensureReportData, exportMatrix, selectedProcess]);

  const handleExportPdf = useCallback(() => {
    if (!ensureReportData()) return;
    setReportExporting(true);
    try {
      const orientation = reportHeaders.length > 5 ? "landscape" : "portrait";
      const doc = new jsPDF({ orientation });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14;
      const headerHeight = 28;
      const usableWidth = pageWidth - margin * 2;
      const cellPaddingX = 3;
      const cellPaddingY = 2.4;
      const lineHeight = 4.2;

      const ensureSafeText = (value) => {
        if (value === undefined || value === null) {
          return "-";
        }
        const stringValue = String(value).trim();
        return stringValue ? stringValue : "-";
      };

      const measureText = (value, { fontSize = 9, fontStyle = "normal" } = {}) => {
        doc.setFont("helvetica", fontStyle);
        doc.setFontSize(fontSize);
        return doc.getTextWidth(ensureSafeText(value));
      };

      const rawWeights = reportHeaders.map((header, columnIndex) => {
        const labelWidth = measureText(header.label, { fontSize: 10, fontStyle: "bold" });
        const descriptionWidth = measureText(header.description, { fontSize: 7 });
        let maxRowWidth = 0;
        reportRows.forEach((row) => {
          const width = measureText(row[columnIndex], { fontSize: 9 });
          if (width > maxRowWidth) {
            maxRowWidth = width;
          }
        });
        const baseWidth = Math.max(labelWidth, descriptionWidth, maxRowWidth);
        return baseWidth + cellPaddingX * 2;
      });

      const totalRawWeight = rawWeights.reduce((sum, weight) => sum + weight, 0);
      let dynamicWidths = rawWeights.map((weight) =>
        totalRawWeight > 0 ? (weight / totalRawWeight) * usableWidth : usableWidth / rawWeights.length,
      );

      const MIN_COLUMN_WIDTH = Math.min(usableWidth / Math.max(reportHeaders.length, 1), 32);
      const totalShortage = dynamicWidths.reduce(
        (shortage, width) => (width < MIN_COLUMN_WIDTH ? shortage + (MIN_COLUMN_WIDTH - width) : shortage),
        0,
      );
      if (totalShortage > 0) {
        const totalSurplus = dynamicWidths.reduce(
          (surplus, width) => (width > MIN_COLUMN_WIDTH ? surplus + (width - MIN_COLUMN_WIDTH) : surplus),
          0,
        );
        if (totalSurplus > 0) {
          dynamicWidths = dynamicWidths.map((width) => {
            if (width < MIN_COLUMN_WIDTH) {
              return MIN_COLUMN_WIDTH;
            }
            const surplus = width - MIN_COLUMN_WIDTH;
            const proportionalReduction = (surplus / totalSurplus) * totalShortage;
            return Math.max(MIN_COLUMN_WIDTH, width - proportionalReduction);
          });
        } else {
          dynamicWidths = dynamicWidths.map(() => usableWidth / Math.max(reportHeaders.length, 1));
        }
      }

      let widthAccumulator = dynamicWidths.reduce((sum, width) => sum + width, 0);
      if (Math.abs(widthAccumulator - usableWidth) > 0.5 && dynamicWidths.length > 0) {
        dynamicWidths[dynamicWidths.length - 1] += usableWidth - widthAccumulator;
      }

      const columnOffsets = [];
      let offset = margin;
      dynamicWidths.forEach((width) => {
        columnOffsets.push(offset);
        offset += width;
      });

      const headerLayouts = reportHeaders.map((header, index) => {
        const innerWidth = Math.max(dynamicWidths[index] - cellPaddingX * 2, 8);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        const labelLines = doc.splitTextToSize(ensureSafeText(header.label), innerWidth);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        const descriptionLines = doc.splitTextToSize(ensureSafeText(header.description), innerWidth);
        return { labelLines, descriptionLines, innerWidth };
      });

      const headerRowHeight = Math.max(
        14,
        ...headerLayouts.map((layout) =>
          (layout.labelLines.length + layout.descriptionLines.length || 1) * lineHeight + cellPaddingY * 2,
        ),
      );

      let cursorY = margin;

      const renderFrame = () => {
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(203, 213, 225);
        doc.roundedRect(margin, margin, usableWidth, headerHeight, 3, 3, "FD");
        let textOffsetX = margin + 12;
        if (logoDataUrl) {
          try {
            const { width: rawWidth, height: rawHeight } = doc.getImageProperties(logoDataUrl);
            if (rawWidth && rawHeight) {
              const scaleFactor = doc.internal.scaleFactor || 1;
              const naturalWidth = rawWidth / scaleFactor;
              const naturalHeight = rawHeight / scaleFactor;
              const maxLogoHeight = headerHeight - 12;
              const maxLogoWidth = usableWidth * 0.25;
              const heightScale = naturalHeight > maxLogoHeight ? maxLogoHeight / naturalHeight : 1;
              const widthScale = naturalWidth > maxLogoWidth ? maxLogoWidth / naturalWidth : 1;
              const appliedScale = Math.min(heightScale, widthScale, 1);
              const logoWidth = naturalWidth * appliedScale;
              const logoHeight = naturalHeight * appliedScale;
              const logoY = margin + headerHeight / 2 - logoHeight / 2;
              doc.addImage(logoDataUrl, "PNG", margin + 10, logoY, logoWidth, logoHeight);
              textOffsetX += logoWidth + 8;
            }
          } catch (imageError) {
            console.error("Falha ao desenhar a logo do relatório", imageError);
          }
        }
        doc.setFont("helvetica", "bold");
        doc.setTextColor(11, 31, 58);
        doc.setFontSize(14);
        doc.text(reportTitle, textOffsetX, margin + 16);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(71, 85, 105);
        doc.text(REPORT_NOTE, textOffsetX, margin + 21);
        doc.setFontSize(9);
        doc.text(`Total de registros filtrados: ${reportRows.length}`, margin, margin + headerHeight + 8);
        if (selectedProcess?.name) {
          doc.text(`Processo: ${selectedProcess.name}`, margin, margin + headerHeight + 14);
        }
        cursorY = margin + headerHeight + 20;
      };

      const renderHeaderRow = () => {
        doc.setDrawColor(11, 31, 58);
        doc.setFillColor(11, 31, 58);
        headerLayouts.forEach((layout, index) => {
          const x = columnOffsets[index];
          const columnWidth = dynamicWidths[index];
          doc.rect(x, cursorY, columnWidth, headerRowHeight, "F");
          let textY = cursorY + cellPaddingY + lineHeight;
          if (layout.labelLines.length) {
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(255, 255, 255);
            layout.labelLines.forEach((line) => {
              doc.text(line, x + cellPaddingX, textY);
              textY += lineHeight;
            });
          }
          if (layout.descriptionLines.length) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.setTextColor(59, 130, 246);
            layout.descriptionLines.forEach((line) => {
              doc.text(line, x + cellPaddingX, textY);
              textY += lineHeight;
            });
          }
        });
        cursorY += headerRowHeight;
      };

      const renderRow = (row, rowIndex) => {
        const lineGroups = row.map((cell, index) => {
          const text = ensureSafeText(cell);
          const maxWidth = Math.max(dynamicWidths[index] - cellPaddingX * 2, 8);
          return doc.splitTextToSize(text, maxWidth);
        });
        const rowHeight = Math.max(
          10,
          ...lineGroups.map((lines) => Math.max(lines.length, 1) * lineHeight + cellPaddingY * 2),
        );
        if (cursorY + rowHeight > pageHeight - margin) {
          doc.addPage({ orientation });
          renderFrame();
          renderHeaderRow();
        }
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        const fillColor = rowIndex % 2 === 0 ? [248, 250, 252] : [241, 245, 249];
        lineGroups.forEach((lines, index) => {
          const x = columnOffsets[index];
          const columnWidth = dynamicWidths[index];
          doc.setFillColor(fillColor[0], fillColor[1], fillColor[2]);
          doc.setDrawColor(226, 232, 240);
          doc.rect(x, cursorY, columnWidth, rowHeight, "FD");
          doc.setTextColor(30, 41, 59);
          let textY = cursorY + cellPaddingY + lineHeight;
          lines.forEach((line) => {
            doc.text(line, x + cellPaddingX, textY);
            textY += lineHeight;
          });
        });
        cursorY += rowHeight;
      };

      renderFrame();
      renderHeaderRow();
      reportRows.forEach((row, index) => {
        renderRow(row, index);
      });

      const filename = buildReportFilename(selectedProcess?.name || "relatorio", "pdf");
      doc.save(filename);
    } catch (error) {
      console.error("Falha ao gerar PDF do relatório", error);
      setReportError("Não foi possível gerar o PDF. Tente novamente.");
    } finally {
      setReportExporting(false);
    }
  }, [
    ensureReportData,
    logoDataUrl,
    reportHeaders,
    reportRows,
    reportTitle,
    selectedProcess,
  ]);

  const handlePrintRecord = useCallback(() => {
    if (!record) {
      return;
    }

    try {
      const doc = new jsPDF({ orientation: "portrait" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 16;
      const contentWidth = pageWidth - margin * 2;
      const lineHeight = 6;

      let cursorY = margin;

      const ensureSpace = (height) => {
        if (cursorY + height > pageHeight - margin) {
          doc.addPage();
          cursorY = margin;
        }
      };

      const title =
        dedicatedPageLabel?.trim() ||
        lockedPageName?.trim() ||
        selectedProcess?.name?.trim() ||
        "Central de Cadastros";

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(15, 23, 42);
      doc.text(title, margin, cursorY + 12);

      let logoBottom = cursorY;
      if (logoDataUrl) {
        try {
          const { width: rawWidth, height: rawHeight } = doc.getImageProperties(logoDataUrl);
          if (rawWidth && rawHeight) {
            const scaleFactor = doc.internal.scaleFactor || 1;
            const naturalWidth = rawWidth / scaleFactor;
            const naturalHeight = rawHeight / scaleFactor;
            const maxWidth = contentWidth * 0.25;
            const maxHeight = 18;
            const widthScale = maxWidth / naturalWidth;
            const heightScale = maxHeight / naturalHeight;
            const appliedScale = Math.min(widthScale, heightScale, 1);
            const logoWidth = naturalWidth * appliedScale;
            const logoHeight = naturalHeight * appliedScale;
            const logoX = pageWidth - margin - logoWidth;
            doc.addImage(logoDataUrl, "PNG", logoX, cursorY, logoWidth, logoHeight);
            logoBottom = cursorY + logoHeight;
          }
        } catch (imageError) {
          console.error("Falha ao inserir a logo na ficha", imageError);
        }
      }

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(71, 85, 105);
      doc.text(`Ficha do cadastro • Solicitação #${record.id}`, margin, cursorY + 19);
      doc.text(`Emitido em ${new Date().toLocaleString("pt-BR")}`, margin, cursorY + 25);

      const headerBottom = Math.max(cursorY + 28, logoBottom + 8);
      cursorY = headerBottom + 4;

      const summaryItems = [
        {
          label: "Processo",
          value:
            (record.process && record.process.name) ||
            selectedProcess?.name ||
            "Não informado",
        },
        {
          label: "Solicitação",
          value: `#${record.id}`,
        },
        {
          label: "Situação",
          value: translateStatus(record.status, record.statusLabel) || "Não informado",
        },
        {
          label: "Início",
          value: formatDateTime(record.startedAt) || "Não informado",
        },
      ];

      if (record.startedBy?.name || record.startedBy?.email) {
        summaryItems.push({
          label: "Solicitante",
          value: record.startedBy.name || record.startedBy.email,
        });
      }

      if (record.protocol || record.requestProtocol) {
        summaryItems.push({
          label: "Protocolo",
          value: record.protocol || record.requestProtocol,
        });
      }

      const renderSummaryRow = (items) => {
        const rowItems = items.filter((item) => item && item.label);
        if (!rowItems.length) {
          return;
        }
        const columnWidth =
          rowItems.length === 1 ? contentWidth : (contentWidth - 6) / rowItems.length;
        const rowHeight = rowItems.reduce((height, item) => {
          const valueLines = doc.splitTextToSize(String(item.value || "Não informado"), columnWidth - 10);
          const blockHeight = valueLines.length * lineHeight + 16;
          return Math.max(height, blockHeight);
        }, 20);
        ensureSpace(rowHeight + 4);
        rowItems.forEach((item, index) => {
          const x = margin + index * (columnWidth + (rowItems.length === 1 ? 0 : 6));
          const labelText = item.label.toUpperCase();
          const valueText = String(item.value || "Não informado");
          const valueLines = doc.splitTextToSize(valueText, columnWidth - 10);
          doc.setDrawColor(226, 232, 240);
          doc.setFillColor(248, 250, 252);
          doc.roundedRect(x, cursorY, columnWidth, rowHeight, 3, 3, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8.5);
          doc.setTextColor(100, 116, 139);
          doc.text(labelText, x + 6, cursorY + 8);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(11);
          doc.setTextColor(30, 41, 59);
          doc.text(valueLines, x + 6, cursorY + 16, {
            maxWidth: columnWidth - 12,
          });
        });
        cursorY += rowHeight + 6;
      };

      for (let i = 0; i < summaryItems.length; i += 2) {
        renderSummaryRow(summaryItems.slice(i, i + 2));
      }

      const describeFile = (file) => {
        if (!file) return "";
        if (typeof file === "string") return file;
        if (typeof file === "object") {
          return (
            file.name ||
            file.originalName ||
            file.fileName ||
            file.title ||
            file.url ||
            "Arquivo"
          );
        }
        return String(file);
      };

      const resolveFieldValue = (field) => {
        if (!field || !field.id) return "Não informado";
        const fieldType = typeof field.type === "string" ? field.type : "text";
        const value =
          editedData?.[field.id] !== undefined
            ? editedData[field.id]
            : record.formData?.[field.id];

        if (FILE_FIELD_TYPES.has(fieldType)) {
          const files = [];
          if (Array.isArray(value)) {
            value.forEach((fileItem) => {
              const label = describeFile(fileItem);
              if (label) files.push(label);
            });
          } else if (value && typeof value === "object" && Array.isArray(value.files)) {
            value.files.forEach((fileItem) => {
              const label = describeFile(fileItem);
              if (label) files.push(label);
            });
          } else if (value) {
            const label = describeFile(value);
            if (label) files.push(label);
          }
          return files.length ? files.join("\n") : "Nenhum arquivo anexado";
        }

        if (fieldType === "checkbox" && typeof value === "boolean") {
          return value ? "Sim" : "Não";
        }

        if (FIELD_TYPES_WITH_OPTIONS.has(fieldType)) {
          const options = normalizeOptions(field.options);
          if (Array.isArray(value)) {
            const mapped = value
              .map((entry) => {
                const option = options.find((item) => String(item.id) === String(entry));
                return option ? option.label : describeValue(entry);
              })
              .filter(Boolean);
            return mapped.length ? mapped.join(", ") : "Não informado";
          }
          const option = options.find((item) => String(item.id) === String(value));
          if (option) {
            return option.label;
          }
        }

        if (fieldType === "date" || fieldType === "datetime") {
          if (!value) {
            return "Não informado";
          }
          const parsed = new Date(value);
          if (!Number.isNaN(parsed.getTime())) {
            return parsed.toLocaleString("pt-BR");
          }
        }

        if (value === null || value === undefined || value === "") {
          return "Não informado";
        }

        const described = describeValue(value);
        return described ? described : "Não informado";
      };

      const renderSectionTitle = (text) => {
        const safeText = text?.trim();
        if (!safeText) {
          return;
        }
        const lines = doc.splitTextToSize(safeText, contentWidth);
        const height = lines.length * lineHeight + 4;
        ensureSpace(height + 4);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(37, 99, 235);
        doc.text(lines, margin, cursorY + lineHeight + 2);
        cursorY += height + 6;
      };

      const renderFieldBlock = (label, valueText) => {
        const safeLabel = label ? label.toUpperCase() : "CAMPO";
        const contentText = valueText && valueText.trim ? valueText.trim() : valueText;
        const displayValue = contentText ? String(contentText) : "Não informado";
        const labelLines = doc.splitTextToSize(safeLabel, contentWidth - 12);
        const valueLines = doc.splitTextToSize(displayValue, contentWidth - 12);
        const blockHeight = labelLines.length * (lineHeight - 1) + valueLines.length * lineHeight + 12;
        ensureSpace(blockHeight + 4);
        doc.setDrawColor(226, 232, 240);
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(margin, cursorY, contentWidth, blockHeight, 3, 3, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(100, 116, 139);
        doc.text(labelLines, margin + 6, cursorY + 8);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.setTextColor(30, 41, 59);
        doc.text(valueLines, margin + 6, cursorY + 14, {
          maxWidth: contentWidth - 12,
        });
        cursorY += blockHeight + 6;
      };

      groupedFields.forEach((group) => {
        if (!group || !Array.isArray(group.fields) || group.fields.length === 0) {
          return;
        }
        if (group.label) {
          renderSectionTitle(group.label);
        }
        group.fields.forEach((field) => {
          const label = resolveFieldLabel(field);
          const value = resolveFieldValue(field);
          renderFieldBlock(label, value);
        });
      });

      if (activeMetadataFields.length) {
        renderSectionTitle("CAMPOS ADICIONAIS");
        activeMetadataFields.forEach((field) => {
          const value = editedMetadata?.[field.id] || null;
          const displayValue = value
            ? formatMetadataValueDisplay(field, value)
            : "Não informado";
          renderFieldBlock(field.label || field.id, displayValue || "Não informado");
        });
      }

      const noteLines = doc.splitTextToSize(RECORD_SHEET_NOTE, contentWidth);
      const noteHeight = noteLines.length * (lineHeight - 1) + 6;
      ensureSpace(noteHeight);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(noteLines, margin, cursorY + noteHeight - 4);

      const filename = buildRecordSheetFilename(
        selectedProcess?.name || record.process?.name || "cadastro",
        record.id,
      );
      doc.save(filename);
    } catch (error) {
      console.error("Falha ao gerar a ficha em PDF", error);
    }
  }, [
    activeMetadataFields,
    dedicatedPageLabel,
    editedData,
    editedMetadata,
    groupedFields,
    lockedPageName,
    logoDataUrl,
    record,
    selectedProcess,
  ]);

  const handleAddProcessPage = useCallback(() => {
    if (
      !selectedProcessId ||
      typeof onCreateRegistrationPage !== "function" ||
      hasExistingPage ||
      !canManageRegistrations
    ) {
      return;
    }

    const process = processes.find(
      (item) => Number(item?.id) === Number(selectedProcessId),
    );

    if (!process) {
      return;
    }

    const result = onCreateRegistrationPage(process);
    if (!result || !result.page) {
      return;
    }

    if (result.created && typeof openTab === "function") {
      const updatedPages = Array.isArray(result.pages)
        ? result.pages
        : registrationPages;
      openTab(
        result.page.id,
        result.page.tabLabel || result.page.label,
        RegistrationCenter,
        {
          openTab,
          registrationPages: updatedPages,
          onCreateRegistrationPage,
          onUpdateRegistrationPage,
          onDeleteRegistrationPage,
          canManageRegistrations,
          initialProcessId: result.page.processId,
          lockedProcessId: result.page.processId,
          registrationPageName: result.page.label,
        },
      );
    }
  }, [
    canManageRegistrations,
    hasExistingPage,
    onCreateRegistrationPage,
    onDeleteRegistrationPage,
    onUpdateRegistrationPage,
    openTab,
    processes,
    registrationPages,
    selectedProcessId,
  ]);

  const handleRenamePage = useCallback(() => {
    if (!dedicatedPage || typeof onUpdateRegistrationPage !== "function") {
      setRenameModalOpen(false);
      return;
    }
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setPageActionError("Informe um nome para a página dedicada.");
      setPageActionSuccess("");
      return;
    }
    const updated = onUpdateRegistrationPage(dedicatedPage.processId, {
      label: trimmed,
      tabLabel: `Central de Cadastros • ${trimmed}`,
    });
    if (updated) {
      setPageActionSuccess("Página dedicada renomeada com sucesso.");
      setPageActionError("");
      setRenameModalOpen(false);
    } else {
      setPageActionError("Não foi possível renomear a página dedicada.");
      setPageActionSuccess("");
    }
  }, [dedicatedPage, onUpdateRegistrationPage, renameValue]);

  const handleChangePageIcon = useCallback(() => {
    if (!dedicatedPage || typeof onUpdateRegistrationPage !== "function") {
      setIconModalOpen(false);
      return;
    }
    const updated = onUpdateRegistrationPage(dedicatedPage.processId, {
      icon: iconSelection,
    });
    if (updated) {
      setPageActionSuccess("Ícone da página dedicado atualizado com sucesso.");
      setPageActionError("");
      setIconModalOpen(false);
    } else {
      setPageActionError("Não foi possível atualizar o ícone da página dedicada.");
      setPageActionSuccess("");
    }
  }, [dedicatedPage, iconSelection, onUpdateRegistrationPage]);

  const handleRemovePage = useCallback(() => {
    if (!dedicatedPage || typeof onDeleteRegistrationPage !== "function") {
      setRemoveModalOpen(false);
      return;
    }
    const removed = onDeleteRegistrationPage(dedicatedPage.processId);
    if (removed) {
      setPageActionSuccess(
        "Página dedicada removida. Você pode acessá-la novamente pela Central de Cadastros principal.",
      );
      setPageActionError("");
      if (typeof closeTab === "function") {
        closeTab();
      }
    } else {
      setPageActionError("Não foi possível remover a página dedicada.");
      setPageActionSuccess("");
    }
    setRemoveModalOpen(false);
  }, [closeTab, dedicatedPage, onDeleteRegistrationPage]);

  const openRenameModal = useCallback(() => {
    clearPageActionFeedback();
    setRenameModalOpen(true);
  }, [clearPageActionFeedback]);

  const openIconModal = useCallback(() => {
    clearPageActionFeedback();
    setIconModalOpen(true);
  }, [clearPageActionFeedback]);

  const openRemoveModal = useCallback(() => {
    clearPageActionFeedback();
    setRemoveModalOpen(true);
  }, [clearPageActionFeedback]);

  const handleFieldChange = useCallback(
    (field, value) => {
      if (!field || !field.id || !canEdit) return;
      setEditedData((prev) => ({ ...prev, [field.id]: value }));
    },
    [canEdit, setEditedData],
  );

  const handleCheckboxChange = useCallback(
    (field) => {
      if (!field || !field.id || !canEdit) return;
      const currentValue = Boolean(editedData[field.id]);
      setEditedData((prev) => ({ ...prev, [field.id]: !currentValue }));
    },
    [canEdit, editedData, setEditedData],
  );

  const handleSave = useCallback(async () => {
    if (!selectedProcessId || !selectedInstanceId || saving || !record) return;
    if (!hasExistingPage) {
      setSaveError("Adicione a página de cadastro para liberar a edição.");
      return;
    }
    if (!meetsStatusRequirement) {
      setSaveError("Somente solicitações concluídas podem ser alteradas.");
      return;
    }
    setSaveError("");
    setSaveSuccess("");
    if (!hasChanges) {
      setSaveError("Nenhuma alteração detectada para salvar.");
      return;
    }
    setSaving(true);

    try {
      const payload = JSON.parse(JSON.stringify(editedData));
      const metadataPayload = JSON.parse(JSON.stringify(editedMetadata));
      const response = await api.put(
        `/cadastros/${selectedProcessId}/instances/${selectedInstanceId}`,
        {
          data: payload,
          metadata: metadataPayload,
        },
      );
      setRecord(response.data);
      setSaveSuccess("Dados atualizados com sucesso.");
      if (historyKey) {
        const serverHistory = Array.isArray(response.data?.editHistory)
          ? response.data.editHistory
          : [];
        setHistoryByInstance((prev) => ({
          ...prev,
          [historyKey]: serverHistory,
        }));
      }
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Não foi possível atualizar os dados da solicitação.";
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  }, [
    editedData,
    editedMetadata,
    hasChanges,
    hasExistingPage,
    historyKey,
    meetsStatusRequirement,
    record,
    saving,
    selectedInstanceId,
    selectedProcessId,
  ]);

  const renderField = useCallback(
    (field) => {
      if (!field || !field.id) return null;
      const value = editedData[field.id];
      const originalValue = initialData[field.id];
      const fieldType = typeof field.type === "string" ? field.type : "text";
      const label = resolveFieldLabel(field);
      const options = FIELD_TYPES_WITH_OPTIONS.has(fieldType)
        ? normalizeOptions(field.options)
        : [];
      const isUnsupported = unsupportedFieldTypes.has(fieldType);
      const isEditable = isPrimitive(originalValue) || isPrimitive(value);
      const isDisabled = !canEdit;

      const commonLabelClass = "block text-sm font-medium text-gray-700";
      const commonInputClass =
        "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500";
      const computedInputClass = `${commonInputClass} ${
        isDisabled ? "cursor-not-allowed bg-gray-100 text-gray-500" : ""
      }`;

      if (FILE_FIELD_TYPES.has(fieldType)) {
        const rawValue = value !== undefined ? value : originalValue;
        let files = [];
        if (Array.isArray(rawValue)) {
          files = rawValue.filter(Boolean);
        } else if (rawValue && typeof rawValue === "object" && Array.isArray(rawValue.files)) {
          files = rawValue.files.filter(Boolean);
        } else if (rawValue) {
          files = [rawValue];
        }

        return (
          <div key={field.id} className="space-y-1">
            <label className={commonLabelClass}>{label}</label>
            {files.length === 0 ? (
              <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-500">
                Nenhum arquivo anexado.
              </div>
            ) : (
              <div className="space-y-2">
                {files.map((fileItem, index) => {
                  const key =
                    (fileItem && typeof fileItem === "object" && (fileItem.id || fileItem.name || fileItem.url)) ??
                    index;
                  return (
                    <FileAttachment
                      key={key}
                      file={fileItem}
                      onOpenFileManager={
                        canOpenFileManager ? handleOpenFileManager : undefined
                      }
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      }

      if (isUnsupported || (!isEditable && !FIELD_TYPES_WITH_OPTIONS.has(fieldType))) {
        return (
          <div key={field.id} className="space-y-1">
            <label className={commonLabelClass}>{label}</label>
            <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-600">
              {value !== undefined ? (
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words text-xs">
                  {JSON.stringify(value, null, 2)}
                </pre>
              ) : (
                <span>Sem dados</span>
              )}
            </div>
          </div>
        );
      }

      if (fieldType === "checkbox" && typeof (originalValue ?? value) === "boolean") {
        return (
          <div key={field.id} className="flex items-center gap-2 py-1">
            <input
              id={`field-${field.id}`}
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={Boolean(value)}
              disabled={isDisabled}
              onChange={() => handleCheckboxChange(field)}
            />
            <label htmlFor={`field-${field.id}`} className="text-sm text-gray-700">
              {label}
            </label>
          </div>
        );
      }

      if (FIELD_TYPES_WITH_OPTIONS.has(fieldType) && options.length > 0) {
        const stringValue = value === undefined || value === null ? "" : String(value);
        return (
          <div key={field.id} className="space-y-1">
            <label htmlFor={`field-${field.id}`} className={commonLabelClass}>
              {label}
            </label>
            <select
              id={`field-${field.id}`}
              className={computedInputClass}
              value={stringValue}
              disabled={isDisabled}
              onChange={(event) => handleFieldChange(field, event.target.value)}
            >
              <option value="">Selecione</option>
              {options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );
      }

      if (fieldType === "textarea") {
        return (
          <div key={field.id} className="space-y-1">
            <label htmlFor={`field-${field.id}`} className={commonLabelClass}>
              {label}
            </label>
            <textarea
              id={`field-${field.id}`}
              className={`${computedInputClass} min-h-[120px]`}
              value={value === undefined || value === null ? "" : String(value)}
              disabled={isDisabled}
              onChange={(event) => handleFieldChange(field, event.target.value)}
            />
          </div>
        );
      }

      const inputType =
        fieldType === "number" || fieldType === "integer" || fieldType === "decimal"
          ? "number"
          : fieldType === "date"
          ? "date"
          : fieldType === "datetime"
          ? "datetime-local"
          : "text";

      return (
        <div key={field.id} className="space-y-1">
          <label htmlFor={`field-${field.id}`} className={commonLabelClass}>
            {label}
          </label>
          <input
            id={`field-${field.id}`}
            type={inputType}
            className={computedInputClass}
            value={value === undefined || value === null ? "" : String(value)}
            disabled={isDisabled}
            onChange={(event) => handleFieldChange(field, event.target.value)}
          />
        </div>
      );
    },
    [
      canEdit,
      editedData,
      handleCheckboxChange,
      handleFieldChange,
      canOpenFileManager,
      handleOpenFileManager,
      initialData,
    ],
  );

  const renderConsultationCard = () => {
    const cardBaseClass = isLockedProcess
      ? "rounded-2xl border border-blue-100 bg-white/90 p-6 shadow-lg shadow-blue-100 backdrop-blur"
      : "rounded-xl bg-white p-6 shadow-sm";
    const filterContainerClass = isLockedProcess
      ? "rounded-xl border border-blue-100 bg-blue-50/70 p-4 shadow-inner"
      : "rounded-xl border border-gray-200 bg-gray-50 p-4";
    const filterInputClass = isLockedProcess
      ? "w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
      : "w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500";
    const highlightNoticeBaseClass = isLockedProcess
      ? "flex items-start gap-3 rounded-xl border px-4 py-3 text-sm shadow-inner"
      : "flex items-start gap-3 rounded-lg border px-3 py-2 text-sm shadow";
    const tableContainerClass = isLockedProcess
      ? "max-h-[440px] overflow-y-auto rounded-xl border border-blue-100 bg-white shadow-sm"
      : "max-h-[420px] overflow-y-auto rounded-lg border border-gray-200";
    const activeRowClass = isLockedProcess
      ? "cursor-pointer bg-blue-100/80"
      : "cursor-pointer bg-blue-50";
    const inactiveRowClass = isLockedProcess
      ? "cursor-pointer hover:bg-blue-50/80"
      : "cursor-pointer hover:bg-gray-50";

    return (
      <div className={cardBaseClass}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Solicitações</h2>
            <p className="text-sm text-gray-600">
              Selecione uma solicitação para visualizar ou alterar seus dados.
            </p>
          </div>
          {selectedProcessId && (
            <div className="flex flex-wrap items-center justify-start gap-2 sm:justify-end">
              <button
                type="button"
                onClick={handleOpenHighlightModal}
                className="inline-flex items-center gap-2 rounded-lg border border-blue-500 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <FiTag className="h-4 w-4" aria-hidden="true" />
                Identificador do dashboard
              </button>
              <button
                type="button"
                onClick={() => handleOpenColumnsModal("consultation")}
                className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-sm font-medium text-blue-600 transition hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <FiSliders className="h-4 w-4" aria-hidden="true" />
                Colunas de consultas e relatórios
              </button>
              <button
                type="button"
                onClick={() => fetchInstances(selectedProcessId)}
                className="inline-flex items-center gap-2 rounded-lg border border-blue-500 bg-white px-3 py-1.5 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={loadingInstances}
              >
                <FiRefreshCw
                  className={`h-4 w-4 ${loadingInstances ? "animate-spin" : ""}`}
                  aria-hidden="true"
                />
                {loadingInstances ? "Atualizando" : "Atualizar"}
              </button>
            </div>
          )}
        </div>

        {selectedProcessId && (
          <div className="mt-4 space-y-3">
            {selectedProcessHighlightField ? (
              <div
                className={`${highlightNoticeBaseClass} ${
                  isLockedProcess
                    ? "border-green-200/80 bg-green-50/90 text-green-900"
                    : "border-green-200 bg-green-50 text-green-800"
                }`}
              >
                <FiTag className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <p>
                  O campo <span className="font-semibold">"{resolveFieldLabel(selectedProcessHighlightField)}"</span>
                  será destacado no dashboard.
                </p>
              </div>
            ) : highlightOptions.length ? (
              <div
                className={`${highlightNoticeBaseClass} ${
                  isLockedProcess
                    ? "border-blue-200/80 bg-blue-50/80 text-blue-900"
                    : "border-blue-200 bg-blue-50 text-blue-800"
                }`}
              >
                <FiInfo className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <p>
                  Escolha um campo preenchido pelo solicitante para identificar as solicitações
                  sem precisar abri-las no dashboard.
                </p>
              </div>
            ) : (
              <div
                className={`${highlightNoticeBaseClass} ${
                  isLockedProcess
                    ? "border-amber-200/80 bg-amber-50/90 text-amber-900"
                    : "border-amber-200 bg-amber-50 text-amber-800"
                }`}
              >
                <FiInfo className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <p>
                  Adicione campos de texto, número ou seleção ao formulário para habilitar um identificador rápido no dashboard.
                </p>
              </div>
            )}
            {highlightSaving && (
              <p className="text-xs font-medium text-blue-600">Salvando preferências...</p>
            )}
          </div>
        )}

        {!selectedProcessId && (
          <p className="mt-4 text-sm text-gray-600">
            Escolha um processo para listar suas solicitações.
          </p>
        )}

        {selectedProcessId && instancesError && (
          <p className="mt-4 text-sm text-red-600">{instancesError}</p>
        )}

        {selectedProcessId && !instancesError && (
          <div className="mt-4 space-y-4">
            <div className={filterContainerClass}>
              <div className="grid gap-4 lg:grid-cols-4">
                <div className="space-y-1">
                  <label htmlFor="statusFilter" className="text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    id="statusFilter"
                    value={activeFilters.status}
                    onChange={handleStatusFilterChange}
                    className={filterInputClass}
                    disabled={!selectedProcessId}
                  >
                    <option value="all">Todos</option>
                    <option value="active">Em andamento</option>
                    <option value="completed">Concluídos</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label htmlFor="queryFilter" className="text-sm font-medium text-gray-700">
                    Busca rápida
                  </label>
                  <input
                    id="queryFilter"
                    type="text"
                    value={activeFilters.query}
                    onChange={handleQueryFilterChange}
                    placeholder="Busque pelos campos exibidos na tabela"
                    className={filterInputClass}
                    disabled={!selectedProcessId}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="startDateFilter" className="text-sm font-medium text-gray-700">
                    Início a partir de
                  </label>
                  <input
                    id="startDateFilter"
                    type="date"
                    value={activeFilters.startDate || ""}
                    onChange={handleDateFilterChange("startDate")}
                    className={filterInputClass}
                    disabled={!selectedProcessId}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="endDateFilter" className="text-sm font-medium text-gray-700">
                    Início até
                  </label>
                  <input
                    id="endDateFilter"
                    type="date"
                    value={activeFilters.endDate || ""}
                    onChange={handleDateFilterChange("endDate")}
                    className={filterInputClass}
                    disabled={!selectedProcessId}
                  />
                </div>
              </div>
              {hasActiveFilters && (
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={clearActiveFilters}
                    className="inline-flex items-center text-sm font-medium text-blue-600 transition hover:text-blue-800"
                  >
                    Limpar filtros
                  </button>
                </div>
              )}
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white/70 p-4 shadow-sm backdrop-blur">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold text-slate-900">Resumo das consultas</h3>
                  <p className="text-xs text-slate-500">
                    {hasReportData
                      ? `${reportRows.length} registro${reportRows.length === 1 ? "" : "s"} prontos para o relatório.`
                      : "Nenhum registro encontrado com os filtros atuais."}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleOpenReportModal}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-violet-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/40 transition hover:shadow-indigo-500/60 disabled:cursor-not-allowed disabled:from-slate-300 disabled:via-slate-400 disabled:to-slate-500 disabled:text-slate-700 disabled:shadow-none"
                    disabled={!hasReportData}
                  >
                    <span>Gerar relatório</span>
                  </button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedProcess?.name && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-100">
                    Processo:
                    <span className="font-normal normal-case text-slate-200">
                      {selectedProcess.name}
                    </span>
                  </span>
                )}
                {activeFilters.status !== "all" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-100">
                    Status:
                    <span className="font-normal normal-case text-slate-200">
                      {activeFilters.status === "completed"
                        ? "Concluídos"
                        : "Em andamento"}
                    </span>
                  </span>
                )}
                {activeFilters.query && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-100">
                    Busca:
                    <span className="font-normal normal-case text-slate-200">
                      “{activeFilters.query}”
                    </span>
                  </span>
                )}
                {activeFilters.startDate && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-100">
                    De:
                    <span className="font-normal normal-case text-slate-200">
                      {formatFilterDateLabel(activeFilters.startDate)}
                    </span>
                  </span>
                )}
                {activeFilters.endDate && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-100">
                    Até:
                    <span className="font-normal normal-case text-slate-200">
                      {formatFilterDateLabel(activeFilters.endDate)}
                    </span>
                  </span>
                )}
              </div>
            </div>
            <div className={tableContainerClass}>
              {summaryColumns.length === 0 && (
                <div className="border-b border-gray-100 bg-yellow-50 px-4 py-3 text-sm text-yellow-700">
                  Nenhuma coluna foi configurada para o quadro de consultas. Clique em "Personalizar colunas" para
                  escolher os campos que deseja visualizar.
                </div>
              )}
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {summaryColumns.map((column) => (
                      <th
                        key={column.id}
                        className="px-4 py-2 text-left font-medium text-gray-600"
                      >
                        {column.label}
                      </th>
                    ))}
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      Situação
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loadingInstances && (
                    <tr>
                      <td
                        colSpan={summaryColumns.length + 1}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        Carregando...
                      </td>
                    </tr>
                  )}
                  {!loadingInstances && filteredInstances.length === 0 && (
                    <tr>
                      <td
                        colSpan={summaryColumns.length + 1}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        {instances.length === 0
                          ? "Nenhuma solicitação encontrada."
                          : "Nenhuma solicitação encontrada para os filtros selecionados."}
                      </td>
                    </tr>
                  )}
                  {!loadingInstances &&
                    filteredInstances.map((instance) => {
                      const isActive = instance.id === selectedInstanceId;
                      const summaryMap = createSummaryMap(instance);
                      const statusLabel = translateStatus(
                        instance.status,
                        instance.statusLabel,
                      );
                      const startedAtLabel = formatDateTime(instance.startedAt);
                      return (
                        <tr
                          key={instance.id}
                          className={isActive ? activeRowClass : inactiveRowClass}
                          onClick={() => handleInstanceSelect(instance.id)}
                        >
                          {summaryColumns.map((column) => {
                            const resolved = resolveInstanceSummaryValue(
                              instance,
                              column,
                              summaryMap,
                            );
                            return (
                              <td
                                key={`${instance.id}-${column.id}`}
                                className="px-4 py-2 text-gray-700"
                              >
                                {resolved !== undefined &&
                                resolved !== null &&
                                String(resolved).trim() !== "" &&
                                resolved !== "-" ? (
                                  resolved
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            );
                          })}
                          <td className="px-4 py-2 text-gray-600">
                            <div className="flex flex-col gap-1">
                              <span className="inline-flex w-fit items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
                                {statusLabel}
                              </span>
                              {startedAtLabel !== "-" && (
                                <span className="text-xs text-gray-500">
                                  Iniciada em {startedAtLabel}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDetailsCard = () => {
    const statusTotals = instances.reduce(
      (acc, instance) => {
        const status = String(instance?.status || "").toUpperCase();
        if (status === "COMPLETED") acc.completed += 1;
        else acc.active += 1;
        acc.total += 1;
        return acc;
      },
      { completed: 0, active: 0, total: 0 },
    );
    const summaryColumns = selectedProcess?.summaryColumns || [];
    const selectedFieldIds = selectedProcessId
      ? dashboardSelectedFieldsByProcess[selectedProcessId] || []
      : [];
    const schemaFieldOptions = Array.isArray(record?.formSchema?.fields)
      ? record.formSchema.fields
          .filter((field) => field && typeof field.id === "string")
          .map((field) => ({
            id: field.id,
            label: resolveFieldLabel(field),
          }))
      : [];
    const mergedFieldMap = new Map();
    [...summaryColumns, ...schemaFieldOptions].forEach((field) => {
      if (!field?.id) return;
      if (!mergedFieldMap.has(field.id)) {
        mergedFieldMap.set(field.id, {
          id: field.id,
          label: field.label || field.id,
        });
      }
    });
    const availableCategoricalFields = Array.from(mergedFieldMap.values());
    const selectedFieldAnalytics = availableCategoricalFields
      .map((column) => {
        const counters = new Map();
        instances.forEach((instance) => {
          const summaryValue = resolveInstanceSummaryValue(instance, column);
          const detailedInstance =
            dashboardInstancesDataByProcess?.[selectedProcessId]?.[instance?.id];
          const raw =
            summaryValue !== undefined && summaryValue !== null && String(summaryValue).trim() !== ""
              ? summaryValue
              : instance?.formData?.[column.id] ?? detailedInstance?.formData?.[column.id];
          const normalizedValue =
            raw !== undefined && raw !== null && String(raw).trim() !== ""
              ? String(raw).trim()
              : null;
          if (!normalizedValue || normalizedValue === "-") return;
          const status = String(instance?.status || "").toUpperCase();
          const current = counters.get(normalizedValue) || {
            label: normalizedValue,
            completed: 0,
            active: 0,
            total: 0,
          };
          current.total += 1;
          if (status === "COMPLETED") current.completed += 1;
          else current.active += 1;
          counters.set(normalizedValue, current);
        });
        const values = Array.from(counters.values()).sort((a, b) => b.total - a.total);
        return {
          id: column.id,
          label: column.label || column.id || "Campo",
          values,
          total: values.reduce((acc, item) => acc + item.total, 0),
        };
      })
      .filter((item) => selectedFieldIds.includes(item.id));
    const statusPieData = {
      labels: ["Concluídos", "Em andamento"],
      datasets: [
        {
          data: [statusTotals.completed, statusTotals.active],
          backgroundColor: ["#10b981", "#3b82f6"],
          borderWidth: 0,
        },
      ],
    };

    const cardWrapperClass = isLockedProcess
      ? "relative overflow-hidden rounded-2xl border border-blue-100 bg-white/80 p-6 shadow-xl"
      : "rounded-xl bg-white p-6 shadow-sm";
    const summaryContainerClass = isLockedProcess
      ? "rounded-xl border border-blue-100 bg-white/70 p-4 text-sm text-gray-700 shadow-inner"
      : "rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700";
    const sectionTitleClass = isLockedProcess
      ? "text-sm font-semibold uppercase tracking-wide text-blue-600"
      : "text-sm font-semibold uppercase tracking-wide text-gray-500";
    const saveButtonBaseClass =
      "rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition focus:outline-none";
    const saveButtonEnabledClass =
      "bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-300 focus:ring-offset-2";
    const saveButtonDisabledClass = isLockedProcess
      ? "cursor-not-allowed bg-blue-300/60"
      : "cursor-not-allowed bg-blue-300";
    const handleAddFieldToDashboard = () => {
      if (!selectedProcessId || !dashboardFieldDraft) return;
      setDashboardSelectedFieldsByProcess((prev) => {
        const current = prev[selectedProcessId] || [];
        if (current.includes(dashboardFieldDraft)) return prev;
        return { ...prev, [selectedProcessId]: [...current, dashboardFieldDraft] };
      });
      setDashboardFieldDraft("");
    };
    const handleRemoveFieldFromDashboard = (fieldId) => {
      if (!selectedProcessId) return;
      setDashboardSelectedFieldsByProcess((prev) => {
        const current = prev[selectedProcessId] || [];
        return { ...prev, [selectedProcessId]: current.filter((item) => item !== fieldId) };
      });
    };

    return (
      <div className={cardWrapperClass}>
        {isLockedProcess && (
          <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-blue-50 via-white to-emerald-50" />
        )}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Detalhes</h2>
            <p className="text-sm text-gray-600">
              Visualize e edite os dados coletados pelo formulário vinculado ao processo.
            </p>
          </div>
          {selectedProcessId && selectedInstanceId && (
            <div className="flex flex-wrap justify-start gap-2 sm:justify-end">
              <button
                type="button"
                onClick={handlePrintRecord}
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-500 bg-emerald-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                disabled={loadingRecord || !record}
              >
                {loadingRecord ? "Preparando..." : "Imprimir ficha"}
              </button>
              <button
                type="button"
                onClick={() => fetchRecord(selectedProcessId, selectedInstanceId)}
                className="rounded-lg border border-blue-500 px-3 py-1.5 text-sm font-medium text-blue-600 transition hover:bg-blue-50"
                disabled={loadingRecord}
              >
                {loadingRecord ? "Carregando..." : "Recarregar"}
              </button>
            </div>
          )}
        </div>
        <div className="mt-4 inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
          <button
            type="button"
            onClick={() => setDetailsViewTab("details")}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${detailsViewTab === "details" ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
          >
            <span className="inline-flex items-center gap-1.5">
              <FiList className="h-4 w-4" aria-hidden="true" />
              Detalhes
            </span>
          </button>
          <button
            type="button"
            onClick={() => setDetailsViewTab("dashboard")}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${detailsViewTab === "dashboard" ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
          >
            <span className="inline-flex items-center gap-1.5">
              <FiBarChart2 className="h-4 w-4" aria-hidden="true" />
              Dashboard
            </span>
          </button>
        </div>

        {!selectedInstanceId && detailsViewTab === "details" && (
          isLockedProcess ? (
            <div className="mt-6 rounded-xl border border-dashed border-blue-200 bg-blue-50/70 p-6 text-center text-sm text-blue-700">
              Selecione uma solicitação no quadro de consultas para visualizar os detalhes.
            </div>
          ) : (
            <p className="mt-4 text-sm text-gray-600">
              Selecione uma solicitação para visualizar os dados do formulário.
            </p>
          )
        )}

        {recordError && <p className="mt-4 text-sm text-red-600">{recordError}</p>}

        {detailsViewTab === "dashboard" && selectedInstanceId && !recordError && (
          <div className="mt-4 space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Concluídos</p>
                <p className="mt-2 text-3xl font-bold text-emerald-600">{statusTotals.completed}</p>
              </div>
              <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Em andamento</p>
                <p className="mt-2 text-3xl font-bold text-blue-600">{statusTotals.active}</p>
              </div>
              <div className="rounded-xl border border-violet-100 bg-gradient-to-br from-violet-50 to-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-violet-700">Total</p>
                <p className="mt-2 text-3xl font-bold text-violet-600">{statusTotals.total}</p>
              </div>
            </div>
            <div className="grid gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-800">Distribuição das solicitações</h3>
                <div className="mx-auto mt-4 max-w-[260px]">
                  <Pie data={statusPieData} options={{ plugins: { legend: { position: "bottom" } } }} />
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-800">Personalizar gráficos de respostas</h3>
                <p className="mt-1 text-xs text-slate-600">Escolha quais campos terão gráfico de pizza (sexo, nome, idade ou qualquer outro).</p>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <select
                    value={dashboardFieldDraft}
                    onChange={(event) => setDashboardFieldDraft(event.target.value)}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">Selecione um campo</option>
                    {availableCategoricalFields.map((field) => (
                      <option key={field.id} value={field.id}>{field.label || field.id}</option>
                    ))}
                  </select>
                  <button type="button" onClick={handleAddFieldToDashboard} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                    Adicionar
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedFieldIds.map((fieldId) => {
                    const field = availableCategoricalFields.find((item) => item.id === fieldId);
                    return (
                      <span key={fieldId} className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs text-blue-700">
                        {field?.label || fieldId}
                        <button type="button" onClick={() => handleRemoveFieldFromDashboard(fieldId)} className="font-semibold text-red-500">x</button>
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800">Gráficos de pizza das respostas selecionadas</h3>
              {dashboardDataLoading && (
                <p className="mt-3 text-xs text-blue-600">Carregando dados completos das respostas...</p>
              )}
              {selectedFieldAnalytics.length === 0 ? (
                <p className="mt-3 text-sm text-slate-600">Selecione ao menos um campo para visualizar o gráfico de respostas.</p>
              ) : (
                <div className="mt-3 grid gap-4 md:grid-cols-2">
                  {selectedFieldAnalytics.map((field) => (
                    <div key={field.id} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{field.label}</p>
                      <p className="mt-1 text-xs text-slate-600">Total de respostas: <span className="font-semibold text-slate-800">{field.total}</span></p>
                      <div className="mx-auto mt-2 max-w-[210px]">
                        <Pie
                          data={{
                            labels: field.values.map((item) => item.label),
                            datasets: [{
                              data: field.values.map((item) => item.total),
                              backgroundColor: ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"],
                              borderWidth: 0,
                            }],
                          }}
                          options={{ plugins: { legend: { position: "bottom" } } }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800">Contabilização por status dos campos selecionados</h3>
              {selectedFieldAnalytics.length === 0 ? (
                <p className="mt-3 text-sm text-slate-600">Sem campos selecionados para contabilização.</p>
              ) : (
                <div className="mt-3 space-y-4">
                  {selectedFieldAnalytics.map((field) => (
                    <div key={field.id} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{field.label}</p>
                      <div className="mt-2 overflow-x-auto">
                        <table className="min-w-full text-left text-xs text-slate-700">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="px-2 py-1.5 font-semibold">Valor</th>
                              <th className="px-2 py-1.5 font-semibold">Concluídas</th>
                              <th className="px-2 py-1.5 font-semibold">Em andamento</th>
                              <th className="px-2 py-1.5 font-semibold">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {field.values.map((value) => (
                              <tr key={`${field.id}-${value.label}`} className="border-b border-slate-100 last:border-0">
                                <td className="px-2 py-1.5 font-medium text-slate-800">{value.label}</td>
                                <td className="px-2 py-1.5 text-emerald-700">{value.completed}</td>
                                <td className="px-2 py-1.5 text-blue-700">{value.active}</td>
                                <td className="px-2 py-1.5 font-semibold text-slate-900">{value.total}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {detailsViewTab === "details" && selectedInstanceId && !recordError && (
          <div className="mt-4 space-y-4">
            {record && (
              <div className={summaryContainerClass}>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <span className="font-medium text-gray-900">Solicitação:</span> #{record.id}
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Status:</span>{' '}
                    {translateStatus(record.status, record.statusLabel)}
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Início:</span> {formatDateTime(record.startedAt)}
                  </div>
                  {record.startedBy && (
                    <div>
                      <span className="font-medium text-gray-900">Solicitante:</span> {record.startedBy.name || record.startedBy.email}
                    </div>
                  )}
                </div>
              </div>
            )}

            {loadingRecord && (
              <p className="text-sm text-gray-600">Carregando dados...</p>
            )}

            {!loadingRecord && record && (
              <div className="space-y-6">
                {!canEdit && (
                  <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-700">
                    {!hasExistingPage
                      ? "Adicione a página de cadastro para liberar a edição."
                      : "As alterações são permitidas apenas para solicitações concluídas."}
                  </div>
                )}
                {displayedGroupedFields.length === 0 ? (
                  <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                    Nenhum campo está visível na página dedicada. Use "Campos da página dedicada" para marcar os campos que deseja exibir.
                  </div>
                ) : (
                  displayedGroupedFields.map((group) => (
                    <div key={group.id} className="space-y-3">
                      {group.label && <h3 className={sectionTitleClass}>{group.label}</h3>}
                      <div className="grid gap-4 md:grid-cols-2">
                        {group.fields.map((field) => renderField(field))}
                      </div>
                    </div>
                  ))
                )}
                <div className="space-y-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className={sectionTitleClass}>Campos adicionais</h3>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={handleOpenDedicatedFieldsModal}
                        className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition hover:border-indigo-300 hover:bg-indigo-100"
                      >
                        <FiColumns className="h-4 w-4" aria-hidden="true" />
                        Campos da página dedicada
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenMetadataModal}
                        className="inline-flex items-center gap-2 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 transition hover:border-blue-400 hover:bg-blue-50"
                      >
                        Gerenciar campos extras
                      </button>
                    </div>
                  </div>
                  {activeMetadataFields.length === 0 ? (
                    <p className="text-sm text-gray-600">
                      Nenhum campo extra configurado. Use "Gerenciar campos extras" para
                      adicionar informações como nome do solicitante, data da solicitação ou
                      vínculos com outros processos.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {activeMetadataFields.map((field) => {
                        const currentValue =
                          editedMetadata?.[field.id] ?? null;
                        const displayValue = formatMetadataValueDisplay(
                          field,
                          currentValue,
                        );
                        return (
                          <div
                            key={field.id}
                            className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                          >
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900">
                                    {field.label}
                                  </span>
                                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-gray-600">
                                    {field.type === "text"
                                      ? "Texto"
                                      : field.type === "date"
                                      ? "Data"
                                      : "Vínculo"}
                                  </span>
                                </div>
                                {field.description && (
                                  <p className="text-xs text-gray-500">
                                    {field.description}
                                  </p>
                                )}
                              </div>
                              {field.type !== "link" && (
                                <button
                                  type="button"
                                  onClick={() => handleClearMetadataField(field.id)}
                                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-gray-500 transition hover:text-red-600"
                                  disabled={!canEdit}
                                >
                                  Limpar
                                </button>
                              )}
                            </div>
                            <div className="mt-3">
                              {field.type === "text" && (
                                <input
                                  type="text"
                                  value={
                                    currentValue && typeof currentValue.value === "string"
                                      ? currentValue.value
                                      : ""
                                  }
                                  onChange={(event) =>
                                    handleMetadataTextChange(field, event.target.value)
                                  }
                                  disabled={!canEdit}
                                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                                  placeholder="Informe o valor para este campo"
                                />
                              )}
                              {field.type === "date" && (
                                <input
                                  type="date"
                                  value={
                                    currentValue && typeof currentValue.value === "string"
                                      ? currentValue.value
                                      : ""
                                  }
                                  onChange={(event) =>
                                    handleMetadataDateChange(field, event.target.value)
                                  }
                                  disabled={!canEdit}
                                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                                />
                              )}
                              {field.type === "link" && (
                                <div className="flex flex-col gap-3 rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-700">
                                  <div>
                                    {displayValue ? (
                                      <span>{displayValue}</span>
                                    ) : (
                                      <span className="text-gray-500">
                                        Nenhum processo vinculado.
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleOpenLinkModal(field)}
                                      className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-white px-3 py-1.5 text-xs font-medium text-blue-600 transition hover:border-blue-400 hover:bg-blue-50"
                                      disabled={!canEdit}
                                    >
                                      Vincular processo
                                    </button>
                                    {currentValue && (
                                      <button
                                        type="button"
                                        onClick={() => handleClearMetadataField(field.id)}
                                        className="inline-flex items-center gap-1 rounded-md border border-transparent bg-red-100 px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-200"
                                        disabled={!canEdit}
                                      >
                                        Remover vínculo
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {(saveError || saveSuccess) && (
              <div>
                {saveError && <p className="text-sm text-red-600">{saveError}</p>}
                {saveSuccess && <p className="text-sm text-green-600">{saveSuccess}</p>}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={!record || !hasChanges || saving || !canEdit}
                className={`${saveButtonBaseClass} ${
                  !record || !hasChanges || saving || !canEdit
                    ? saveButtonDisabledClass
                    : saveButtonEnabledClass
                }`}
              >
                {saving ? "Salvando..." : "Salvar alterações"}
              </button>
              {hasChanges && !saving && (
                <span className="text-xs text-gray-500">Existem alterações não salvas.</span>
              )}
            </div>
            <div className="space-y-3">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <h3 className={sectionTitleClass}>Histórico de alterações</h3>
                {editHistory.length > 0 && (
                  <span className="text-xs text-gray-500">
                    Histórico persistido das alterações realizadas na Central de Cadastros.
                  </span>
                )}
              </div>
              <div className="max-h-60 space-y-3 overflow-y-auto pr-1">
                {editHistory.length === 0 ? (
                  <p className="text-sm text-gray-600">
                    Nenhuma alteração registrada para este cadastro até o momento.
                  </p>
                ) : (
                  editHistory.map((entry) => {
                    const entryChanges = Array.isArray(entry.changes)
                      ? entry.changes
                      : [];
                    return (
                      <div
                        key={entry.id}
                        className="rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-700 shadow-sm"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2 text-gray-500">
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">
                              {formatDateTime(entry.timestamp)}
                            </span>
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                              por {entry.userName || entry.userEmail || "Usuário não identificado"}
                            </span>
                          </div>
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                            {entryChanges.length} alteração{entryChanges.length === 1 ? "" : "es"}
                          </span>
                        </div>
                        <ul className="mt-2 list-inside list-disc space-y-1 text-gray-600">
                          {entryChanges.map((change, index) => (
                            <li key={`${entry.id}-${change.fieldId}-${index}`} className="space-y-0.5">
                              <div className="flex flex-wrap items-center gap-x-2">
                                <span className="font-medium text-gray-900">{change.label}</span>
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                  {change.scope === "metadata" ? "Campo extra" : "Formulário"} • {change.fieldId}
                                </span>
                              </div>
                              <div className="text-gray-600">
                                {change.from} → {change.to}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="space-y-6 bg-slate-50 p-6">
        <div className="flex flex-col gap-6 text-slate-900 md:flex-row md:items-center md:justify-between">
          <div className="space-y-3">
            {isLockedProcess ? (
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                Cadastro dedicado
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
                <span className="inline-block h-2 w-2 rounded-full bg-blue-500" />
                Hub de cadastros
              </span>
            )}
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">{headerTitle}</h1>
            <p className="max-w-2xl text-sm text-slate-600">{headerSubtitle}</p>
            {isLockedProcess && dedicatedPage && (
              <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
                <DedicatedMenuIcon className="text-base" />
                <span>Ícone exibido no menu</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            {loadingProcesses && (
              <span className="text-sm text-gray-500">Carregando processos...</span>
            )}
          </div>
        </div>

        {(settingsError || settingsSuccess) && (
          <div className="space-y-2">
            {settingsError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                {settingsError}
              </div>
            )}
            {settingsSuccess && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
                {settingsSuccess}
              </div>
            )}
          </div>
        )}

        {isLockedProcess && dedicatedPage && canManageRegistrations && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={openRenameModal}
              className="rounded-lg border border-blue-500 px-3 py-1.5 text-sm font-medium text-blue-600 transition hover:bg-blue-50"
            >
              Renomear página
            </button>
            <button
              type="button"
              onClick={openIconModal}
              className="rounded-lg border border-blue-500 px-3 py-1.5 text-sm font-medium text-blue-600 transition hover:bg-blue-50"
            >
              Alterar ícone
            </button>
            <button
              type="button"
              onClick={openRemoveModal}
              className="rounded-lg border border-red-500 px-3 py-1.5 text-sm font-medium text-red-600 transition hover:bg-red-50"
            >
              Remover página
            </button>
          </div>
        )}

        {(pageActionError || pageActionSuccess) && (
          <div className="space-y-1">
            {pageActionError && <p className="text-sm text-red-600">{pageActionError}</p>}
            {pageActionSuccess && <p className="text-sm text-green-600">{pageActionSuccess}</p>}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-[minmax(0,400px)_max-content] md:items-end">
          <div className="space-y-2">
            <label htmlFor="processSelect" className="text-sm font-medium text-gray-700">
              Processo
            </label>
            <select
              id="processSelect"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
              value={selectedProcessId ?? ""}
              onChange={handleProcessChange}
              disabled={isLockedProcess}
            >
              <option value="">Selecione um processo</option>
              {processOptions.map((process) => (
                <option key={process.id} value={process.id}>
                  {process.name}
                  {process.formName ? ` • ${process.formName}` : ""}
                </option>
              ))}
            </select>
            {processError && <p className="text-sm text-red-600">{processError}</p>}
          </div>
          {!isLockedProcess && (
            <div className="flex justify-start md:justify-end">
              <button
                type="button"
                onClick={handleAddProcessPage}
                className="rounded-lg border border-blue-500 px-4 py-2 text-sm font-medium text-blue-600 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={!canAddProcessPage}
                title={
                  !selectedProcessId
                    ? "Selecione um processo para adicionar uma página"
                    : hasExistingPage
                    ? "Este processo já possui uma página dedicada"
                    : typeof onCreateRegistrationPage !== "function"
                    ? "Recurso indisponível no momento"
                    : undefined
                }
              >
                Adicionar Página
              </button>
            </div>
          )}
        </div>

        {isLockedProcess ? (
          <div className="space-y-6">
            {renderDetailsCard()}
            {detailsViewTab === "details" && renderConsultationCard()}
          </div>
        ) : (
          <div className={`grid gap-6 ${detailsViewTab === "details" ? "lg:grid-cols-2" : ""}`}>
            {detailsViewTab === "details" && renderConsultationCard()}
            {renderDetailsCard()}
          </div>
        )}
      </div>

      <Modal
        open={reportModalOpen}
        onClose={handleCloseReportModal}
        size="max-w-5xl"
      >
        <div className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white/95 p-6 text-slate-700 shadow-2xl">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-1">
                <div className="flex w-full items-center gap-4 rounded-3xl bg-[#0b1f3a]/5 px-4 py-3">
                  <div className="flex h-14 w-14 flex-none items-center justify-center rounded-2xl bg-white shadow-inner ring-1 ring-slate-200">
                    <img
                      src="/nashlogoazul.png"
                      alt="Nash Tecnologia"
                      className="h-10 w-10 object-contain"
                    />
                  </div>
                  <div className="space-y-1">
                    <h2 className="text-2xl font-semibold leading-tight text-[#0b1f3a]">
                      {reportTitle}
                    </h2>
                    <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
                      {REPORT_NOTE}
                    </p>
                    <p className="text-sm text-slate-600">
                      Visualize as solicitações filtradas e escolha o formato ideal para exportar.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleExportPdf}
                  disabled={reportExporting || !hasReportData}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:shadow-emerald-500/60 disabled:cursor-not-allowed disabled:from-slate-500 disabled:to-slate-500 disabled:text-slate-200 disabled:shadow-none"
                >
                  {reportExporting ? "Gerando..." : "Exportar PDF"}
                </button>
                <button
                  type="button"
                  onClick={handleExportXls}
                  disabled={reportExporting || !hasReportData}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-sky-500 to-blue-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-sky-500/40 transition hover:shadow-sky-500/60 disabled:cursor-not-allowed disabled:from-slate-500 disabled:to-slate-500 disabled:text-slate-200 disabled:shadow-none"
                >
                  {reportExporting ? "Gerando..." : "Exportar XLS"}
                </button>
                <button
                  type="button"
                  onClick={handleExportXlsx}
                  disabled={reportExporting || !hasReportData}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/40 transition hover:shadow-indigo-500/60 disabled:cursor-not-allowed disabled:from-slate-500 disabled:to-slate-500 disabled:text-slate-200 disabled:shadow-none"
                >
                  {reportExporting ? "Gerando..." : "Exportar XLSX"}
                </button>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-500">
              {selectedProcess?.name && (
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 font-semibold uppercase tracking-wide">
                  Processo
                  <span className="font-normal normal-case">{selectedProcess.name}</span>
                </span>
              )}
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 font-semibold uppercase tracking-wide">
                Registros
                <span className="font-normal normal-case">{reportRows.length}</span>
              </span>
              {activeFilters.status !== "all" && (
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 font-semibold uppercase tracking-wide">
                  Status
                  <span className="font-normal normal-case">
                    {activeFilters.status === "completed" ? "Concluídos" : "Em andamento"}
                  </span>
                </span>
              )}
              {activeFilters.query && (
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 font-semibold uppercase tracking-wide">
                  Busca
                  <span className="font-normal normal-case">“{activeFilters.query}”</span>
                </span>
              )}
              {activeFilters.startDate && (
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 font-semibold uppercase tracking-wide">
                  Desde
                  <span className="font-normal normal-case">
                    {formatFilterDateLabel(activeFilters.startDate)}
                  </span>
                </span>
              )}
              {activeFilters.endDate && (
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 font-semibold uppercase tracking-wide">
                  Até
                  <span className="font-normal normal-case">
                    {formatFilterDateLabel(activeFilters.endDate)}
                  </span>
                </span>
              )}
            </div>
          </div>
          {reportError && (
            <p className="text-sm text-red-500">{reportError}</p>
          )}
          <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-inner">
            {hasReportData ? (
              <div className="max-h-80 overflow-auto">
                <table className="min-w-full table-auto border-separate border-spacing-0 text-left text-xs text-slate-700">
                  <thead className="sticky top-0 bg-[#0b1f3a] text-[11px] uppercase tracking-wide text-white">
                    <tr>
                      {reportHeaders.map((header) => (
                        <th
                          key={header.id}
                          className="border-b border-[#13315b] px-4 py-3 align-middle"
                          scope="col"
                        >
                          <div className="flex flex-col gap-1">
                            <span className="text-[13px] font-semibold text-white">
                              {header.label}
                            </span>
                            <span className="text-[10px] font-medium uppercase tracking-wide text-white/70">
                              {header.description}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportRows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="odd:bg-white even:bg-slate-50/70">
                        {row.map((value, cellIndex) => (
                          <td
                            key={`${rowIndex}-${cellIndex}`}
                            className="border-b border-slate-200 px-4 py-3 align-top text-[13px] text-slate-700"
                          >
                            <span className="block whitespace-pre-wrap break-words">{value}</span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center py-12 text-sm text-slate-500">
                Nenhuma informação disponível para o relatório.
              </div>
            )}
          </div>
        </div>
      </Modal>
      <Modal open={highlightModalOpen} onClose={handleCloseHighlightModal}>
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
            <FiTag className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Identificador exibido no dashboard</h2>
            <p className="mt-1 text-sm text-slate-600">
              Escolha qual campo preenchido pelo solicitante deve aparecer nas notificações e cartões do dashboard.
            </p>
          </div>
        </div>
        {highlightOptions.length ? (
          <div className="space-y-2">
            <label htmlFor="dashboardHighlightField" className="block text-sm font-medium text-slate-700">
              Campo destacado
            </label>
            <select
              id="dashboardHighlightField"
              value={
                highlightOptions.some(
                  (field) => field.id === selectedProcessHighlightField?.id,
                )
                  ? selectedProcessHighlightField?.id
                  : ""
              }
              onChange={handleHighlightSelection}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={highlightSaving}
            >
              <option value="">Usar apenas o número da solicitação</option>
              {highlightOptions.map((field) => (
                <option key={field.id} value={field.id}>
                  {resolveFieldLabel(field)}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500">
              Esta configuração é opcional. Se nenhum campo for selecionado, o dashboard exibirá somente o número da solicitação.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            Configure campos de texto, número ou seleção no formulário deste processo para habilitar o identificador do dashboard.
          </div>
        )}
        {highlightSaving && (
          <p className="text-xs font-medium text-blue-600">Salvando preferências...</p>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleCloseHighlightModal}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
          >
            Fechar
          </button>
        </div>
      </Modal>
      <Modal open={columnsModalState.open} onClose={handleCloseColumnsModal}>
        <div>
          <h2 className="text-xl font-semibold text-slate-900">
            {columnsModalState.context === "dedicated"
              ? "Personalizar colunas da página dedicada"
              : "Personalizar colunas de consultas e relatórios"}
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            {columnsModalState.context === "dedicated"
              ? "Defina quais colunas ficarão visíveis na página dedicada deste cadastro."
              : "Escolha os campos que aparecerão no quadro de consultas e nos relatórios deste cadastro."}
          </p>
        </div>
        {selectedProcess ? (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,280px)_minmax(0,320px)]">
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-slate-900">Campos disponíveis</h3>
              <p className="text-xs text-slate-500">
                Inclua informações do formulário, campos extras ou dados do sistema.
              </p>
              <div className="max-h-72 space-y-4 overflow-y-auto pr-1">
                {Object.keys(availableColumnsBySource).length === 0 ? (
                  <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-600">
                    Todos os campos disponíveis já estão selecionados.
                  </div>
                ) : (
                  Object.entries(availableColumnsBySource).map(([source, fields]) => (
                    <div key={source} className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {COLUMN_SOURCE_LABELS[source] || "Campos"}
                      </p>
                      <div className="flex flex-col gap-2">
                        {fields.map((field) => (
                          <button
                            key={field.id}
                            type="button"
                            onClick={() => handleAddColumnToDraft(field)}
                            className="flex flex-col items-start gap-1 rounded-lg border border-blue-100 bg-white px-3 py-2 text-left text-xs text-slate-700 transition hover:border-blue-300 hover:bg-blue-50"
                          >
                            <span className="text-sm font-medium text-slate-800">
                              {field.label || field.fieldId}
                            </span>
                            <span className="text-xs text-slate-500">
                              {COLUMN_SOURCE_LABELS[field.source] || "Campo"} • {field.fieldId}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-slate-900">Colunas selecionadas</h3>
              <p className="text-xs text-slate-500">
                Ajuste o título e a ordem em que os dados serão exibidos.
              </p>
              <div className="space-y-3">
                {columnsModalState.draft.length === 0 ? (
                  <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-600">
                    Nenhum campo selecionado. Adicione campos na coluna ao lado.
                  </div>
                ) : (
                  columnsModalState.draft.map((column, index) => (
                    <div
                      key={column.id}
                      className="space-y-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                    >
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-600">
                          Título da coluna
                        </label>
                        <input
                          type="text"
                          value={column.label || ""}
                          onChange={(event) =>
                            handleUpdateColumnLabel(column.id, event.target.value)
                          }
                          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Informe o título que será exibido"
                        />
                      </div>
                      <p className="text-xs text-slate-500">
                        {COLUMN_SOURCE_LABELS[column.source] || "Campo"} • {column.fieldId}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleMoveColumnInDraft(column.id, -1)}
                          className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={index === 0}
                        >
                          Subir
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveColumnInDraft(column.id, 1)}
                          className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={index === columnsModalState.draft.length - 1}
                        >
                          Descer
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveColumnFromDraft(column.id)}
                          className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50"
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-600">
            Selecione um processo para editar as colunas disponíveis.
          </p>
        )}
        {columnsModalState.error && (
          <p className="text-sm text-red-600">{columnsModalState.error}</p>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCloseColumnsModal}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSaveColumns}
            disabled={columnsModalState.saving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {columnsModalState.saving ? "Salvando..." : "Salvar colunas"}
          </button>
        </div>
      </Modal>

      <Modal open={dedicatedFieldsModalState.open} onClose={handleCloseDedicatedFieldsModal}>
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Campos da página dedicada</h2>
          <p className="mt-1 text-sm text-slate-600">
            Por padrão todos os campos aparecem. Desmarque os campos que não devem ser exibidos na tela do cadastro dedicado.
          </p>
        </div>
        <div className="mt-4 max-h-80 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3">
          {Object.keys(dedicatedFieldsModalState.selection).length === 0 ? (
            <p className="text-sm text-slate-600">Nenhum campo disponível para configurar.</p>
          ) : (
            dedicatedFieldOptions.map((fieldOption) => {
              const checked = dedicatedFieldsModalState.selection[fieldOption.id];
              const field = dataFieldDefinitions.get(fieldOption.id);
              const label = field ? resolveFieldLabel(field) : fieldOption.label;
              return (
                <label
                  key={`dedicated-visible-field-${fieldOption.id}`}
                  className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    checked={Boolean(checked)}
                    onChange={() => handleToggleDedicatedField(fieldOption.id)}
                  />
                  <span>{label}</span>
                </label>
              );
            })
          )}
        </div>
        {dedicatedFieldsModalState.error && (
          <p className="mt-3 text-sm text-red-600">{dedicatedFieldsModalState.error}</p>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCloseDedicatedFieldsModal}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
            disabled={dedicatedFieldsModalState.saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSaveDedicatedVisibleFields}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
            disabled={dedicatedFieldsModalState.saving}
          >
            {dedicatedFieldsModalState.saving ? "Salvando..." : "Salvar visibilidade"}
          </button>
        </div>
      </Modal>

      <Modal open={metadataModalState.open} onClose={handleCloseMetadataModal}>
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Campos extras</h2>
          <p className="mt-1 text-sm text-slate-600">
            Acrescente informações que complementam o cadastro, como nome do solicitante ou vínculos com outros processos.
          </p>
        </div>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {metadataSuggestions.map((suggestion) => (
              <button
                key={suggestion.label}
                type="button"
                onClick={() => handleAddMetadataSuggestion(suggestion)}
                className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 transition hover:border-blue-400 hover:bg-blue-100"
              >
                + {suggestion.label}
              </button>
            ))}
            <button
              type="button"
              onClick={handleAddMetadataField}
              className="rounded-full border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
            >
              + Campo personalizado
            </button>
          </div>
          <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
            {metadataModalState.draft.length === 0 ? (
              <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-600">
                Nenhum campo extra configurado. Utilize os botões acima para adicionar sugestões ou criar um campo personalizado.
              </div>
            ) : (
              metadataModalState.draft.map((field, index) => (
                <div
                  key={`metadata-field-${index}-${field.id || "novo"}`}
                  className="space-y-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-slate-600">
                        Nome do campo
                      </label>
                      <input
                        type="text"
                        value={field.label || ""}
                        onChange={(event) =>
                          handleUpdateMetadataDraft(index, { label: event.target.value })
                        }
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Ex.: Nome do solicitante"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-slate-600">
                        Tipo de informação
                      </label>
                      <select
                        value={field.type || "text"}
                        onChange={(event) =>
                          handleUpdateMetadataDraft(index, { type: event.target.value })
                        }
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="text">Texto livre</option>
                        <option value="date">Data</option>
                        <option value="link">Vínculo com outro processo</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-slate-600">
                      Descrição (opcional)
                    </label>
                    <textarea
                      value={field.description || ""}
                      onChange={(event) =>
                        handleUpdateMetadataDraft(index, {
                          description: event.target.value,
                        })
                      }
                      className="min-h-[80px] w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Explique como o campo deve ser preenchido"
                    />
                  </div>
                  {field.type === "link" && (
                    <p className="text-xs text-slate-500">
                      Este campo permitirá vincular dados de outro processo diretamente na ficha.
                    </p>
                  )}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleRemoveMetadataField(index)}
                      className="rounded border border-red-200 px-3 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50"
                    >
                      Remover campo
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        {metadataModalState.error && (
          <p className="text-sm text-red-600">{metadataModalState.error}</p>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCloseMetadataModal}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSaveMetadataFields}
            disabled={metadataModalState.saving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {metadataModalState.saving ? "Salvando..." : "Salvar campos extras"}
          </button>
        </div>
      </Modal>

      <Modal open={linkModalState.open} onClose={handleCloseLinkModal}>
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Vincular processo relacionado</h2>
          <p className="mt-1 text-sm text-slate-600">
            Selecione um processo, uma solicitação e o campo que será exibido em "{linkModalState.field?.label || "Campo"}".
          </p>
        </div>
        {linkModalCurrentDisplay && (
          <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
            Atualmente vinculado a: <span className="font-semibold">{linkModalCurrentDisplay}</span>
          </div>
        )}
        <div className="grid gap-6 lg:grid-cols-[minmax(0,280px)_minmax(0,320px)]">
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Processo relacionado
              </label>
              <select
                value={linkModalState.processId ?? ""}
                onChange={(event) =>
                  handleSelectLinkProcess(normalizeProcessIdValue(event.target.value))
                }
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Selecione</option>
                {processOptions.map((process) => (
                  <option key={process.id} value={process.id}>
                    {typeof process.name === "string" && process.name.trim()
                      ? process.name.trim()
                      : `Processo #${process.id}`}
                    {typeof process.formName === "string" && process.formName.trim()
                      ? ` • ${process.formName.trim()}`
                      : ""}
                  </option>
                ))}
              </select>
              {linkModalSelectedProcess && (
                <p className="text-xs text-slate-500">
                  Vinculando dados do processo {" "}
                  <span className="font-medium text-slate-600">
                    {linkModalSelectedProcess.name || `#${linkModalSelectedProcess.id}`}
                  </span>
                </p>
              )}
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Buscar solicitação
              </label>
              <input
                type="text"
                value={linkModalState.search}
                onChange={(event) => handleLinkSearchChange(event.target.value)}
                placeholder="Filtre por número ou conteúdo"
                disabled={!linkModalState.processId || linkModalState.instancesLoading}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
              />
            </div>
            <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
              {!linkModalState.processId ? (
                <p className="px-3 py-4 text-sm text-slate-600">
                  Escolha um processo para listar as solicitações disponíveis.
                </p>
              ) : linkModalState.instancesLoading ? (
                <p className="px-3 py-4 text-sm text-slate-600">Carregando solicitações...</p>
              ) : filteredLinkInstances.length === 0 ? (
                <p className="px-3 py-4 text-sm text-slate-600">
                  Nenhuma solicitação encontrada para os filtros informados.
                </p>
              ) : (
                filteredLinkInstances.map((instance) => {
                  const isActive =
                    Number(linkModalState.instanceId) === Number(instance.id);
                  return (
                    <button
                      key={instance.id}
                      type="button"
                      onClick={() =>
                        handleSelectLinkInstance(normalizeProcessIdValue(instance.id))
                      }
                      className={`flex w-full flex-col items-start gap-1 border-b px-3 py-3 text-left text-xs transition last:border-b-0 ${
                        isActive
                          ? "bg-blue-50 font-medium text-blue-700"
                          : "hover:bg-slate-50 text-slate-700"
                      }`}
                    >
                      <span className="text-sm font-semibold">
                        Solicitação #{instance.id}
                      </span>
                      <span className="text-xs text-slate-500">
                        {translateStatus(instance.status, instance.statusLabel)}
                        {instance.startedAt ? ` • ${formatDateTime(instance.startedAt)}` : ""}
                      </span>
                      {Array.isArray(instance.formSummary) && instance.formSummary.length > 0 && (
                        <span className="max-h-10 overflow-hidden text-xs text-slate-500">
                          {instance.formSummary
                            .map((entry) =>
                              entry && entry.label
                                ? `${entry.label}: ${entry.value}`
                                : "",
                            )
                            .filter(Boolean)
                            .slice(0, 3)
                            .join(" • ")}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Campo para vincular
              </label>
              <select
                value={linkModalState.fieldId || ""}
                onChange={(event) => handleSelectLinkField(event.target.value)}
                disabled={!linkModalState.processId || !linkModalState.instanceId || linkModalAvailableFields.length === 0}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
              >
                <option value="">Selecione</option>
                {linkModalAvailableFields.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.label}
                  </option>
                ))}
              </select>
              {linkModalState.processId && linkModalAvailableFields.length === 0 && (
                <p className="text-xs text-slate-500">
                  Não encontramos campos disponíveis neste processo para realizar o vínculo.
                </p>
              )}
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              {linkModalState.detailsLoading ? (
                <p className="text-sm text-slate-600">
                  Carregando detalhes da solicitação selecionada...
                </p>
              ) : linkModalDetails ? (
                <div className="space-y-3 text-sm text-slate-700">
                  <div>
                    <p className="font-semibold text-slate-900">
                      Solicitação #{linkModalDetails.id}
                    </p>
                    <p className="text-xs text-slate-500">
                      {translateStatus(linkModalDetails.status, linkModalDetails.statusLabel)}
                      {linkModalDetails.startedAt
                        ? ` • ${formatDateTime(linkModalDetails.startedAt)}`
                        : ""}
                    </p>
                  </div>
                  {Array.isArray(linkModalDetails.formSummary) && linkModalDetails.formSummary.length > 0 && (
                    <div className="space-y-1 text-xs text-slate-600">
                      {linkModalDetails.formSummary.slice(0, 4).map((entry) => (
                        <div
                          key={entry.id || `${entry.label}-${entry.value}`}
                          className="flex justify-between gap-2"
                        >
                          <span className="font-medium text-slate-700">
                            {entry.label || entry.id}
                          </span>
                          <span className="max-w-[160px] truncate text-right">
                            {entry.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {linkModalFieldPreview && (
                    <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                      Valor encontrado no campo selecionado: {" "}
                      <span className="font-semibold">{linkModalFieldPreview}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-600">
                  Escolha uma solicitação para visualizar os detalhes e selecionar o campo desejado.
                </p>
              )}
            </div>
          </div>
        </div>
        {linkModalState.error && (
          <p className="text-sm text-red-600">{linkModalState.error}</p>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCloseLinkModal}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirmLinkSelection}
            disabled={
              !linkModalState.processId ||
              !linkModalState.instanceId ||
              !linkModalState.fieldId ||
              linkModalState.detailsLoading
            }
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            Confirmar vínculo
          </button>
        </div>
      </Modal>

      <Modal open={renameModalOpen} onClose={() => setRenameModalOpen(false)}>
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Renomear página dedicada</h2>
          <p className="mt-1 text-sm text-slate-600">
            Defina como esta página aparecerá no menu e na aba da Central de Cadastros.
          </p>
        </div>
        <div>
          <label htmlFor="rename-page-input" className="block text-sm font-medium text-slate-700">
            Novo nome
          </label>
          <input
            id="rename-page-input"
            type="text"
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Ex.: Cadastros de fornecedores"
          />
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setRenameModalOpen(false)}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleRenamePage}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            Salvar
          </button>
        </div>
      </Modal>

      <Modal open={iconModalOpen} onClose={() => setIconModalOpen(false)}>
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Escolher ícone</h2>
          <p className="mt-1 text-sm text-slate-600">
            Selecione o ícone que será exibido no menu para esta página dedicada.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {REGISTRATION_PAGE_ICON_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition ${
                iconSelection === option.value
                  ? "border-blue-500 bg-blue-50"
                  : "border-slate-200 bg-white hover:border-blue-300"
              }`}
            >
              <input
                type="radio"
                name="registration-page-icon"
                value={option.value}
                checked={iconSelection === option.value}
                onChange={() => setIconSelection(option.value)}
                className="hidden"
              />
              <option.Icon className="text-2xl text-blue-600" />
              <span className="text-sm font-medium text-slate-700">{option.label}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setIconModalOpen(false)}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleChangePageIcon}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            Salvar ícone
          </button>
        </div>
      </Modal>

      <Modal open={removeModalOpen} onClose={() => setRemoveModalOpen(false)}>
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Remover página dedicada</h2>
          <p className="mt-1 text-sm text-slate-600">
            A página dedicada <strong>{dedicatedPageLabel || lockedPageName}</strong> será removida do menu. Os cadastros existentes permanecerão disponíveis na Central de Cadastros principal.
          </p>
          <p className="mt-3 text-sm font-medium text-red-600">
            Esta ação pode ser revertida apenas criando novamente a página dedicada para este processo.
          </p>
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setRemoveModalOpen(false)}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleRemovePage}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
          >
            Remover página
          </button>
        </div>
      </Modal>

    </>
  );
}

export default RegistrationCenter;
